import { AlertTriangle, Check, Info, Undo2 } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import {
    getForecastDemandPlanningData,
    updateForecastDemandPlanning,
    updateForecastDemandPlanRevenueTarget,
    setForecastDemandPlanningData,
    fetchForecastDemandPlanningList,
} from "../../slices/demandPlanningSlice";
import { getBudgetCards } from "../../slices/quickbooks/budgetSlice";
import { SectionForecastDemand } from "./forecastDemandTableComponents/SectionForecastDemand";
import { MetaFieldForecastDemand } from "./forecastDemandTableComponents/MetafieldForecastDemand";
import { DateFieldForecastDemand } from "./forecastDemandTableComponents/DatefieldForecastDemand";
import { getYearsFromToCurrent } from "../../helpers/common";
import { formatForecastDemandPlanningCreatedDisplay } from "../../helpers/forecastDemandPlanningFormat";
import { SelectDropdown } from "../SelectDropdown";
import { RevenueInputForecastDemand } from "./forecastDemandTableComponents/RevenueInputForecastDemand";
import { CappedPctInputForecastDemand } from "./forecastDemandTableComponents/CappedPctInputForecastDemand";
import { ValidationBadgeForecastDemand } from "./forecastDemandTableComponents/ValidationBadgeForecastDemand";
import { 
    MAX_HISTORY,
    fmtPct,
    fmt$,
    channelTarget,
    fmtDelta,
    deltaClass,
    categoryTarget,
    monthlyTarget,
    finalMonthPct,
    subCatTarget,
} from "../../consts/demandplanning";
import { DemandPlanningLoadingKeys } from "../../slices/demandPlanningSlice";
import { LoaderSpinner } from "../LoaderSpinner";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ConfrimModal } from "../ConfrimModal";

const formatTargetDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
};

const formatLegacyBasisLabel = (basisConversion) => {
    const previousTarget = basisConversion.previous_target;
    const numericTarget = typeof previousTarget === 'number'
        ? previousTarget
        : (typeof previousTarget === 'string' && previousTarget.trim() ? Number(previousTarget) : NaN);
    const label = Number.isFinite(numericTarget)
        ? `Workbook basis (legacy, post-discount): ${fmt$(numericTarget)}`
        : 'Workbook basis (legacy, post-discount)';
    const details = [label];
    const convertedAt = formatTargetDate(basisConversion.converted_at);
    if (convertedAt) details.push(`converted ${convertedAt}`);
    if (basisConversion.budget_id != null && String(basisConversion.budget_id).trim()) {
        details.push(`with Budget ${basisConversion.budget_id} discount drivers`);
    }
    return details.join(' · ');
};

const normNotes = (v) => (v == null ? "" : String(v).trim());

/** LTM % display — never show 0.00% when catalog metrics are not loaded yet. */
const fmtLtmPct = (fraction) =>
    fraction == null || !Number.isFinite(fraction) ? "—" : fmtPct(fraction);

const FORECAST_ADD_CATEGORY_PANEL_W = 288;
const FORECAST_ADD_SUBCATEGORY_PANEL_W = 320;

/** Prefer opening the panel to the RIGHT of the anchor (→ over data columns); flip left if needed. */
function computeAddDropdownLeft(rect, panelWidth, rightInset = 0) {
    const gap = 4;
    const edge = 8;
    const maxLeft = window.innerWidth - panelWidth - edge - rightInset;
    let left = rect.right + gap;
    if (left <= maxLeft) {
        return Math.max(edge, left) - 40;
    }
    left = rect.left - panelWidth - gap;
    if (left >= edge) {
        return left - 40;
    }
    return Math.max(edge, Math.min(maxLeft, rect.right + gap)) - 40;
}

/** Per–forecast-year buckets: { "2026": { api, table } }; api = GET forecast-demand-planning-data payload; table = editable UI rows. */
const normalizePlanningData = (raw) => {
    if (raw == null) return {};
    if (typeof raw === "string") {
        try {
            const p = JSON.parse(raw);
            return p && typeof p === "object" ? p : {};
        } catch {
            return {};
        }
    }
    return typeof raw === "object" ? raw : {};
};

/** Persisted `data[year].api` without a full catalog must not skip GET (empty merge → no silent save). */
function savedApiSnapshotLooksComplete(api) {
    if (!api || typeof api !== "object") return false;
    const ch = Array.isArray(api.channels) ? api.channels.length : 0;
    const cat = Array.isArray(api.categories) ? api.categories.length : 0;
    const sub = Array.isArray(api.subcategories) ? api.subcategories.length : 0;
    const mo = Array.isArray(api.months) ? api.months.length : 0;
    return ch > 0 && cat > 0 && sub > 0 && mo > 0;
}

/** When `meta.forecast_year` is unset (first paint), allow merge + initial persist from GET payload. */
function forecastPayloadMatchesUiYear(d, metaForecastYearStr) {
    if (!d || d.forecastYear === undefined || d.forecastYear === null) return false;
    if (
        metaForecastYearStr === undefined ||
        metaForecastYearStr === null ||
        String(metaForecastYearStr).trim() === ""
    ) {
        return true;
    }
    return String(d.forecastYear) === String(metaForecastYearStr);
}

const channelLabelFromApiKey = (key) => {
    if (key === "shopify") return "Shopify";
    if (key === "amazon") return "Amazon";
    const s = String(key || "").trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};

function channelApiKeyFromLabel(label) {
    const s = String(label || "").trim();
    if (s === "Shopify") return "shopify";
    if (s === "Amazon") return "amazon";
    if (!s) return "unknown";
    return s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * Rewrites `apiJson` catalog arrays to match `tableSnapshot` row counts/order before saving `data[year].api`.
 * Without this, shorter Redux API payloads caused merge effects to drop rows after undo or partial saves.
 */
function alignForecastApiJsonWithTableSnapshot(apiJson, tableSnapshot) {
    const chByLabel = new Map((apiJson.channels || []).map((c) => [channelLabelFromApiKey(c.key), c]));
    apiJson.channels = tableSnapshot.channels.map((row) => {
        const existing = chByLabel.get(row.channel);
        const key = row.channelKey ?? existing?.key ?? channelApiKeyFromLabel(row.channel);
        return {
            key,
            ltmPercentage: existing?.ltmPercentage ?? 0,
            channelPercentage: existing?.channelPercentage ?? null,
            channelRevenueTarget: existing?.channelRevenueTarget ?? null,
        };
    });

    const catByName = new Map((apiJson.categories || []).map((c) => [c.categoryName, c]));
    apiJson.categories = tableSnapshot.categories.map((row) => {
        const name = row.category;
        const existing = catByName.get(name);
        const ltm =
            row.ltmPercentage != null && Number.isFinite(Number(row.ltmPercentage))
                ? Number(row.ltmPercentage)
                : existing?.ltmPercentage ?? 0;
        const actuals =
            row.actuals_2025 != null && Number.isFinite(Number(row.actuals_2025))
                ? Number(row.actuals_2025)
                : existing?.actuals ?? 0;
        const ytd =
            row.ytd_actuals != null && Number.isFinite(Number(row.ytd_actuals))
                ? Number(row.ytd_actuals)
                : existing?.ytd_actuals ?? 0;
        return {
            categoryId: row.categoryId ?? existing?.categoryId ?? null,
            categoryName: name,
            ltmPercentage: ltm,
            actuals,
            ytd_actuals: ytd,
        };
    });

    const subById = new Map((apiJson.subcategories || []).map((s) => [String(s.subcategoryId), s]));
    apiJson.subcategories = tableSnapshot.subCategories.map((row) => {
        const sid = String(row.id);
        const existing = subById.get(sid);
        const rawId = row.subcategoryId ?? row.id;
        const idNum = Number(rawId);
        const ltm =
            row.ltmPercentage != null && Number.isFinite(Number(row.ltmPercentage))
                ? Number(row.ltmPercentage)
                : existing?.ltmPercentage ?? 0;
        const actuals =
            row.actuals_2025 != null && Number.isFinite(Number(row.actuals_2025))
                ? Number(row.actuals_2025)
                : existing?.actuals ?? 0;
        const ytd =
            row.ytd_actuals != null && Number.isFinite(Number(row.ytd_actuals))
                ? Number(row.ytd_actuals)
                : existing?.ytd_actuals ?? 0;
        return {
            subcategoryId: Number.isFinite(idNum) ? idNum : existing?.subcategoryId ?? rawId,
            subcategoryName: row.sub_category ?? existing?.subcategoryName ?? "",
            categoryId: row.categoryId ?? existing?.categoryId ?? null,
            categoryName: row.category ?? existing?.categoryName ?? "",
            ltmPercentage: ltm,
            actuals,
            ytd_actuals: ytd,
        };
    });

    const moByKey = new Map((apiJson.months || []).map((m) => [m.key, m]));
    apiJson.months = tableSnapshot.months.map((row) => {
        const key = row.month;
        const existing = moByKey.get(key);
        const hist =
            row.historical_pct != null && Number.isFinite(Number(row.historical_pct))
                ? Number(row.historical_pct) * 100
                : existing?.historicalPercentage ?? 0;
        const actPct =
            row.actual_pct != null && Number.isFinite(Number(row.actual_pct))
                ? Number(row.actual_pct) * 100
                : existing?.actualPercentage ?? 0;
        return {
            key,
            historicalPercentage: hist,
            overridePercentage: existing?.overridePercentage ?? null,
            finalPercentage: existing?.finalPercentage ?? null,
            monthlyRevenueTarget: existing?.monthlyRevenueTarget ?? null,
            actualPercentage: actPct,
            actualRevenue:
                row.actual_revenue != null && Number.isFinite(Number(row.actual_revenue))
                    ? Number(row.actual_revenue)
                    : existing?.actualRevenue ?? 0,
        };
    });
}

function lastClosedDateLabelForForecastYear(forecastYear) {
    if (forecastYear == null || forecastYear === "") return "—";
    const now = new Date();
    const currentYear = now.getFullYear();
    const fy = Number(forecastYear);
    if (!Number.isFinite(fy)) return "—";
    if (fy === currentYear) {
        const lastMonthDate = new Date(currentYear, now.getMonth(), 0);
        return lastMonthDate.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    }
    const lastDayOfYear = new Date(fy, 12, 0);
    return lastDayOfYear.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

/**
 * Same row shapes as API→table merge with no prior edits. Lets initial `data[year]` persist run in the same
 * tick as GET (merge useEffects run after this effect and no longer block the save).
 */
function buildTableRowsFromForecastApiPayload(d, metaForecastYearStr) {
    const lcd = lastClosedDateLabelForForecastYear(metaForecastYearStr);
    const channels = (d.channels || []).map((api) => ({
        channel: channelLabelFromApiKey(api.key),
        pct: null,
        channelKey: api.key,
    }));
    const categories = (d.categories || []).map((api) => ({
        category: api.categoryName,
        categoryId: api.categoryId ?? null,
        pct: null,
        actuals_2025: api.actuals,
        ytd_actuals:
            api.ytd_actuals != null && Number.isFinite(Number(api.ytd_actuals))
                ? Number(api.ytd_actuals)
                : 0,
        last_closed_date: lcd,
        ltmPercentage:
            api.ltmPercentage != null && Number.isFinite(Number(api.ltmPercentage))
                ? Number(api.ltmPercentage)
                : null,
    }));
    const subCategories = (d.subcategories || []).map((api) => ({
        id: String(api.subcategoryId),
        subcategoryId: api.subcategoryId,
        sub_category: api.subcategoryName,
        category: api.categoryName ?? "",
        categoryId: api.categoryId ?? null,
        pct_of_channel: null,
        actuals_2025: api.actuals,
        ytd_actuals:
            api.ytd_actuals != null && Number.isFinite(Number(api.ytd_actuals))
                ? Number(api.ytd_actuals)
                : 0,
        last_closed_date: lcd,
        ltmPercentage:
            api.ltmPercentage != null && Number.isFinite(Number(api.ltmPercentage))
                ? Number(api.ltmPercentage)
                : null,
    }));
    const months = (d.months || []).map((api) => ({
        month: api.key,
        historical_pct:
            api.historicalPercentage != null && Number.isFinite(Number(api.historicalPercentage))
                ? Number(api.historicalPercentage) / 100
                : null,
        override_pct: null,
        actual_pct:
            api.actualPercentage != null && Number.isFinite(Number(api.actualPercentage))
                ? Number(api.actualPercentage) / 100
                : null,
        actual_revenue:
            api.actualRevenue != null && Number.isFinite(Number(api.actualRevenue))
                ? Number(api.actualRevenue)
                : null,
    }));
    return { channels, categories, subCategories, months };
}

/** Mirror server `rebuildForecastDemandTableFromApi` — refresh actuals, keep user % overrides. */
function rebuildTableRowsFromForecastApiPayload(api, priorTable, metaForecastYearStr) {
    const prior = priorTable && typeof priorTable === "object" ? priorTable : {};
    const lcd = lastClosedDateLabelForForecastYear(metaForecastYearStr);

    const pctByChannel = new Map((prior.channels || []).map((row) => [row.channel, row.pct]));
    const channels = (api.channels || []).map((apiRow) => {
        const label = channelLabelFromApiKey(apiRow.key);
        return {
            channel: label,
            pct: pctByChannel.has(label) ? pctByChannel.get(label) : null,
            channelKey: apiRow.key,
        };
    });

    const pctByCategory = new Map((prior.categories || []).map((row) => [row.category, row.pct]));
    const categories = (api.categories || []).map((freshCat) => ({
        category: freshCat.categoryName,
        categoryId: freshCat.categoryId ?? null,
        pct: pctByCategory.has(freshCat.categoryName) ? pctByCategory.get(freshCat.categoryName) : null,
        actuals_2025: freshCat.actuals,
        ytd_actuals:
            freshCat.ytd_actuals != null && Number.isFinite(Number(freshCat.ytd_actuals))
                ? Number(freshCat.ytd_actuals)
                : 0,
        last_closed_date:
            (prior.categories || []).find((row) => row.category === freshCat.categoryName)
                ?.last_closed_date ?? lcd,
        ltmPercentage:
            freshCat.ltmPercentage != null && Number.isFinite(Number(freshCat.ltmPercentage))
                ? Number(freshCat.ltmPercentage)
                : null,
    }));

    const pctBySubcategoryId = new Map(
        (prior.subCategories || []).map((row) => [String(row.id), row.pct_of_channel])
    );
    const subCategories = (api.subcategories || []).map((apiRow) => {
        const id = String(apiRow.subcategoryId);
        return {
            id,
            subcategoryId: apiRow.subcategoryId,
            sub_category: apiRow.subcategoryName,
            category: apiRow.categoryName ?? "",
            categoryId: apiRow.categoryId ?? null,
            pct_of_channel: pctBySubcategoryId.has(id) ? pctBySubcategoryId.get(id) : null,
            actuals_2025: apiRow.actuals,
            ytd_actuals:
                apiRow.ytd_actuals != null && Number.isFinite(Number(apiRow.ytd_actuals))
                    ? Number(apiRow.ytd_actuals)
                    : 0,
            last_closed_date:
                (prior.subCategories || []).find((row) => String(row.id) === id)?.last_closed_date ?? lcd,
            ltmPercentage:
                apiRow.ltmPercentage != null && Number.isFinite(Number(apiRow.ltmPercentage))
                    ? Number(apiRow.ltmPercentage)
                    : null,
        };
    });

    const overrideByMonth = new Map((prior.months || []).map((row) => [row.month, row.override_pct]));
    const months = (api.months || []).map((apiRow) => ({
        month: apiRow.key,
        historical_pct:
            apiRow.historicalPercentage != null && Number.isFinite(Number(apiRow.historicalPercentage))
                ? Number(apiRow.historicalPercentage) / 100
                : null,
        override_pct: overrideByMonth.has(apiRow.key) ? overrideByMonth.get(apiRow.key) : null,
        actual_pct:
            apiRow.actualPercentage != null && Number.isFinite(Number(apiRow.actualPercentage))
                ? Number(apiRow.actualPercentage) / 100
                : null,
        actual_revenue:
            apiRow.actualRevenue != null && Number.isFinite(Number(apiRow.actualRevenue))
                ? Number(apiRow.actualRevenue)
                : null,
    }));

    return { channels, categories, subCategories, months };
}

/** Same calendar rule as `isForecastMonthClosed` in the table — for serializing derived metrics into `data.table`. */
function isForecastYearMonthClosedForPersist(forecastYear, monthIndex0to11) {
    const fy =
        forecastYear != null && forecastYear !== ""
            ? Number.parseInt(String(forecastYear), 10)
            : NaN;
    if (!Number.isFinite(fy) || monthIndex0to11 < 0 || monthIndex0to11 > 11) return false;
    const now = new Date();
    const cy = now.getFullYear();
    const cm = now.getMonth();
    if (fy < cy) return true;
    if (fy > cy) return false;
    return monthIndex0to11 < cm;
}

function computeYtdForecastTotalForPersist(months, revenueTarget, forecastYearStr) {
    const fy =
        forecastYearStr != null && forecastYearStr !== ""
            ? Number.parseInt(String(forecastYearStr), 10)
            : NaN;
    if (!Number.isFinite(fy)) return 0;
    return months.reduce((sum, m, idx) => {
        if (!isForecastYearMonthClosedForPersist(fy, idx)) return sum;
        return sum + monthlyTarget(revenueTarget, m);
    }, 0);
}

/**
 * Attach derived values shown in the grid (targets, YTD forecast, deltas) so `data.table` matches the UI.
 */
function enrichForecastTableSnapshotForDataColumn({
    channels,
    categories,
    subCategories,
    months,
    revenueTarget,
    forecastYearStr,
}) {
    const ytdForecastTotal = computeYtdForecastTotalForPersist(months, revenueTarget, forecastYearStr);

    const fyParsed =
        forecastYearStr != null && forecastYearStr !== ""
            ? Number.parseInt(String(forecastYearStr), 10)
            : NaN;

    const enrichedChannels = channels.map((ch) => ({
        ...ch,
        revenue_target: channelTarget(revenueTarget, ch.pct ?? 0),
    }));

    const enrichedCategories = categories.map((cat) => {
        const pct = cat.pct ?? 0;
        const revenue_target = categoryTarget(revenueTarget, pct);
        const ytd_forecast = Math.round(ytdForecastTotal * pct);
        return {
            ...cat,
            revenue_target,
            delta_vs_2025: revenue_target - (cat.actuals_2025 ?? 0),
            ytd_forecast,
            ytd_delta: (cat.ytd_actuals ?? 0) - ytd_forecast,
        };
    });

    const enrichedSubCategories = subCategories.map((sc) => {
        const poc = sc.pct_of_channel ?? 0;
        const catPct = categories.find((c) => c.category === sc.category)?.pct ?? 0;
        const categoryRevTarget = categoryTarget(revenueTarget, catPct);
        const revenue_target = subCatTarget(categoryRevTarget, poc);
        const ytd_forecast = Math.round(ytdForecastTotal * catPct * poc);
        return {
            ...sc,
            revenue_target,
            delta_vs_2025: revenue_target - (sc.actuals_2025 ?? 0),
            ytd_forecast,
            ytd_delta: (sc.ytd_actuals ?? 0) - ytd_forecast,
        };
    });

    const enrichedMonths = months.map((m, idx) => {
        const final_pct = finalMonthPct(m);
        const revenue_target = monthlyTarget(revenueTarget, m);
        const closed =
            Number.isFinite(fyParsed) && isForecastYearMonthClosedForPersist(fyParsed, idx);
        const actualRev = m.actual_revenue;
        const revenue_delta =
            closed && actualRev != null && actualRev !== undefined
                ? actualRev - revenue_target
                : null;
        return {
            ...m,
            final_pct,
            revenue_target,
            month_closed: closed,
            revenue_delta,
        };
    });

    return {
        channels: enrichedChannels,
        categories: enrichedCategories,
        subCategories: enrichedSubCategories,
        months: enrichedMonths,
    };
}

export const ForecastDemandTable = ({
    selectedForecastDemandPlanning,
    salesSnapshotVersion = 0,
    onServerRecordUpdated,
    onEffectiveForecastYearChange,
    marginFromSidebar
}) => {
    const dispatch = useDispatch();

    const {
        forecastDemandPlanningData,
        forecastCatalogGetError,
        loadingKeys
    } = useSelector((state) => state.demandPlanning);
    const budgetCards = useSelector((state) => state.budget?.budgetCards || []);

    const planningId = selectedForecastDemandPlanning?.id;
    const basisConversion = forecastDemandPlanningData?.basis_conversion
        || selectedForecastDemandPlanning?.basis_conversion;
    const isLoadingForecastDemandPlanningData = loadingKeys.includes(DemandPlanningLoadingKeys.getForecastDemandPlanningData);
    const lastSavedRef = useRef({
        notes: "",
        forecastYear: "",
        revenueTarget: 0,
    });
    /** Skip API→table merge effects when hydrating from saved `data[year]`. */
    const suppressApiTableMergeRef = useRef(false);
    /** One load path per planning + year + saved-row fingerprint (re-run when list sync adds `data[year]`). */
    const lastForecastLoadKeyRef = useRef("");
    /** Prevents API→table merge effect from looping when some catalog arrays stay empty. */
    const mergedForecastPayloadSigRef = useRef("");
    /** After API load or partial `data` (api only), persist `{ api, table }` once when UI rows match the API. */
    const pendingInitialDataBucketRef = useRef(null);
    const planningRowRef = useRef(selectedForecastDemandPlanning);

    const [deleteCategory, setDeleteCategory] = useState(null);
    const [deleteSubCategory, setDeleteSubCategory] = useState(null);
    const [deleteSaving, setDeleteSaving] = useState(false);
    /** Forecast GET payload used for fallback hydration + preserving API metrics on add. */
    const [catalogForAdd, setCatalogForAdd] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const catalogKeyRef = useRef("");
    /** Full computed forecast catalog (ignores saved year API) used to re-add with first-load metrics. */
    const [fullCatalogForAdd, setFullCatalogForAdd] = useState(null);
    const fullCatalogKeyRef = useRef("");
    /** Master product-catalog universe (ProductCategoryGroup + ProductCategoryVariant) for add menus. */
    const [masterCatalogForAdd, setMasterCatalogForAdd] = useState(null);
    const masterCatalogLoadedRef = useRef(false);
    const [categoryAddOpen, setCategoryAddOpen] = useState(false);
    const [subcategoryAddOpen, setSubcategoryAddOpen] = useState(false);
    const [categoryAddQuery, setCategoryAddQuery] = useState("");
    const [subcategoryAddQuery, setSubcategoryAddQuery] = useState("");
    const categoryAddAnchorRef = useRef(null);
    const categoryAddPortalRef = useRef(null);
    const subcategoryAddAnchorRef = useRef(null);
    const subcategoryAddPortalRef = useRef(null);
    const [categoryDropdownPos, setCategoryDropdownPos] = useState({ top: 0, left: 0 });
    const [subcategoryDropdownPos, setSubcategoryDropdownPos] = useState({ top: 0, left: 0 });
    /** After catalog GET fails, show + so the user can open the menu to retry. */
    const [history, setHistory] = useState([]);
    const historyRef = useRef([]);
    const [meta, setMeta] = useState(() => ({
        forecast_year:
            selectedForecastDemandPlanning?.forecastYear != null
                ? String(selectedForecastDemandPlanning.forecastYear)
                : "",
        notes: selectedForecastDemandPlanning?.notes ?? "",
    }));
    const [channels, setChannels] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [months, setMonths] = useState([]);
    const [revenueTarget, setRevenueTargetState] = useState(() =>
        Number(selectedForecastDemandPlanning?.revenueTarget) || 0
    );
    const [pendingRevenueTarget, setPendingRevenueTarget] = useState(null);
    const [targetBudgetNames, setTargetBudgetNames] = useState([]);
    const [targetBudgetLookupLoading, setTargetBudgetLookupLoading] = useState(false);
    const [targetBudgetLookupError, setTargetBudgetLookupError] = useState(null);
    const [targetSaving, setTargetSaving] = useState(false);
    const [targetError, setTargetError] = useState(null);
    const [targetResult, setTargetResult] = useState(null);
    /** Latest grid rows for refresh fallback when `data[year].table` is not persisted yet. */
    const localTableSnapshotRef = useRef({
        channels: [],
        categories: [],
        subCategories: [],
        months: [],
    });
    localTableSnapshotRef.current = { channels, categories, subCategories, months };

    /**
     * Single source for API keys / loadKey — resolved in render so we are not one tick behind `setMeta`
     * after selecting a row (first open after create often had meta "" while the row already had forecastYear).
     */
    const effectiveForecastYearStr = useMemo(() => {
        const fromMeta = meta?.forecast_year;
        if (fromMeta !== undefined && fromMeta !== null && String(fromMeta).trim() !== "") {
            return String(fromMeta).trim();
        }
        const fromRow = selectedForecastDemandPlanning?.forecastYear;
        if (fromRow != null && fromRow !== "" && String(fromRow).trim() !== "") {
            return String(fromRow).trim();
        }
        const d = forecastDemandPlanningData;
        if (
            d &&
            planningId != null &&
            String(d.id) === String(planningId) &&
            d.forecastYear != null &&
            String(d.forecastYear).trim() !== ""
        ) {
            return String(d.forecastYear).trim();
        }
        return "";
    }, [meta?.forecast_year, selectedForecastDemandPlanning?.forecastYear, forecastDemandPlanningData, planningId]);

    useEffect(() => {
        if (typeof onEffectiveForecastYearChange === 'function' && effectiveForecastYearStr) {
            onEffectiveForecastYearChange(effectiveForecastYearStr);
        }
    }, [effectiveForecastYearStr, onEffectiveForecastYearChange]);

    /** Stable key so list/API id number-vs-string does not re-trigger row reset every render. */
    const selectedPlanningIdKey =
        selectedForecastDemandPlanning?.id != null && selectedForecastDemandPlanning?.id !== ""
            ? String(selectedForecastDemandPlanning.id)
            : "";

    /**
     * When `data[year]` appears (GET persist or list refetch), the load effect must run again even if
     * planningId + year are unchanged.
     */
    const savedBucketFingerprint = useMemo(() => {
        if (planningId == null || String(planningId).trim() === "" || !effectiveForecastYearStr) {
            return "na";
        }
        const byYear = normalizePlanningData(selectedForecastDemandPlanning?.data);
        const bucket = byYear[effectiveForecastYearStr];
        if (bucket?.api && bucket?.table) return "full";
        if (bucket?.api && savedApiSnapshotLooksComplete(bucket.api)) return "api_ok";
        return "none";
    }, [planningId, effectiveForecastYearStr, selectedForecastDemandPlanning?.data]);

    /** Full snapshot of `data[year]` so catalog reload runs when persisted JSON changes but fingerprint stays `full`. */
    const savedYearBucketDigest = useMemo(() => {
        if (planningId == null || String(planningId).trim() === "" || !effectiveForecastYearStr) {
            return "";
        }
        const byYear = normalizePlanningData(selectedForecastDemandPlanning?.data);
        const bucket = byYear[effectiveForecastYearStr];
        if (bucket == null) return "∅";
        try {
            return JSON.stringify(bucket);
        } catch {
            return "err";
        }
    }, [planningId, effectiveForecastYearStr, selectedForecastDemandPlanning?.data]);

    useEffect(() => {
        mergedForecastPayloadSigRef.current = "";
    }, [planningId, effectiveForecastYearStr, savedYearBucketDigest]);

    /** After sales snapshot refresh, apply fresh actuals while keeping saved (or in-session) % overrides. */
    useEffect(() => {
        if (!salesSnapshotVersion || !planningId || !effectiveForecastYearStr) return;
        const d = forecastDemandPlanningData;
        if (!d || String(d.id) !== String(planningId)) return;
        if (!forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)) return;

        suppressApiTableMergeRef.current = true;
        mergedForecastPayloadSigRef.current = "";
        lastForecastLoadKeyRef.current = "";

        const byYear = normalizePlanningData(selectedForecastDemandPlanning?.data);
        const bucket = byYear[effectiveForecastYearStr];

        if (bucket?.table) {
            setChannels(bucket.table.channels || []);
            setCategories(bucket.table.categories || []);
            setSubCategories(bucket.table.subCategories || []);
            setMonths(bucket.table.months || []);
        } else {
            const built = rebuildTableRowsFromForecastApiPayload(
                d,
                localTableSnapshotRef.current,
                effectiveForecastYearStr
            );
            setChannels(built.channels);
            setCategories(built.categories);
            setSubCategories(built.subCategories);
            setMonths(built.months);
        }

        queueMicrotask(() => {
            suppressApiTableMergeRef.current = false;
        });
    }, [
        salesSnapshotVersion,
        forecastDemandPlanningData,
        planningId,
        effectiveForecastYearStr,
        selectedForecastDemandPlanning?.data,
    ]);

    useEffect(() => {
        if (!selectedPlanningIdKey) return;
        const r = selectedForecastDemandPlanning;
        lastSavedRef.current = {
            notes: r.notes ?? "",
            forecastYear: r.forecastYear != null ? String(r.forecastYear) : "",
            revenueTarget: Number(r.revenueTarget) || 0,
        };
        setMeta({
            forecast_year: lastSavedRef.current.forecastYear,
            notes: lastSavedRef.current.notes,
        });
        setRevenueTargetState(lastSavedRef.current.revenueTarget);
        setChannels([]);
        setCategories([]);
        setSubCategories([]);
        setMonths([]);
        setHistory([]);
        setPendingRevenueTarget(null);
        setTargetBudgetNames([]);
        setTargetBudgetLookupError(null);
        setTargetError(null);
        setTargetResult(null);
        setTargetBudgetLookupLoading(false);
        setTargetSaving(false);
        historyRef.current = [];
        lastForecastLoadKeyRef.current = "";
        dispatch(setForecastDemandPlanningData(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when selected row *id* changes, not when list refetches the same id
    }, [selectedPlanningIdKey, dispatch]);

    /** Row may gain `forecastYear` after list/create refresh without id changing — unlock load + persist. */
    useEffect(() => {
        if (!selectedForecastDemandPlanning?.id) return;
        const raw = selectedForecastDemandPlanning.forecastYear;
        if (raw == null || raw === "") return;
        const serverY = String(raw);
        setMeta((prev) => {
            if (prev.forecast_year !== "") return prev;
            return { ...prev, forecast_year: serverY };
        });
    }, [selectedForecastDemandPlanning?.id, selectedForecastDemandPlanning?.forecastYear]);

    useEffect(() => {
        planningRowRef.current = selectedForecastDemandPlanning;
    }, [selectedForecastDemandPlanning]);

    /** Server may persist `data` on GET; refresh list and merge fresh actuals into the grid. */
    const prevCatalogGetLoadingRef = useRef(false);
    useEffect(() => {
        if (isLoadingForecastDemandPlanningData) {
            prevCatalogGetLoadingRef.current = true;
            return;
        }
        if (!prevCatalogGetLoadingRef.current) return;
        prevCatalogGetLoadingRef.current = false;

        const d = forecastDemandPlanningData;
        if (
            d &&
            planningId != null &&
            String(d.id) === String(planningId) &&
            forecastPayloadMatchesUiYear(d, effectiveForecastYearStr) &&
            Array.isArray(d.channels) &&
            d.channels.length > 0
        ) {
            const hasLocalRows =
                channels.length > 0 &&
                categories.length > 0 &&
                subCategories.length > 0 &&
                months.length > 0;
            const built = hasLocalRows
                ? rebuildTableRowsFromForecastApiPayload(
                      d,
                      localTableSnapshotRef.current,
                      effectiveForecastYearStr
                  )
                : buildTableRowsFromForecastApiPayload(d, effectiveForecastYearStr);
            mergedForecastPayloadSigRef.current = [
                String(planningId),
                effectiveForecastYearStr,
                String(d.id),
                (d.channels || []).length,
                (d.categories || []).length,
                (d.subcategories || []).length,
                (d.months || []).length,
            ].join("|");
            setChannels(built.channels);
            setCategories(built.categories);
            setSubCategories(built.subCategories);
            setMonths(built.months);

            const catalogKey = `${planningId}|${effectiveForecastYearStr}`;
            setCatalogForAdd(d);
            catalogKeyRef.current = catalogKey;
        }

        if (
            forecastDemandPlanningData &&
            planningId != null &&
            String(forecastDemandPlanningData.id) === String(planningId)
        ) {
            void dispatch(fetchForecastDemandPlanningList());
        }
    }, [
        dispatch,
        isLoadingForecastDemandPlanningData,
        forecastDemandPlanningData,
        planningId,
        effectiveForecastYearStr,
        channels.length,
        categories.length,
        subCategories.length,
        months.length,
    ]);

    useEffect(() => {
        setCatalogForAdd(null);
        catalogKeyRef.current = "";
        setFullCatalogForAdd(null);
        fullCatalogKeyRef.current = "";
        setCategoryAddOpen(false);
        setSubcategoryAddOpen(false);
        setCategoryAddQuery("");
        setSubcategoryAddQuery("");
    }, [planningId, effectiveForecastYearStr]);

    /** Undo snapshots are per forecast year; switching year must not restore another year's rows into `data`. */
    useEffect(() => {
        setHistory([]);
        historyRef.current = [];
    }, [effectiveForecastYearStr]);

    useEffect(() => {
        if (!planningId || !effectiveForecastYearStr) {
            return;
        }
        const persistKey = `${planningId}|${effectiveForecastYearStr}`;
        const catalogLoadKey = `${persistKey}|${savedBucketFingerprint}|${savedYearBucketDigest}`;
        if (lastForecastLoadKeyRef.current === catalogLoadKey) {
            return;
        }
        lastForecastLoadKeyRef.current = catalogLoadKey;

        const byYear = normalizePlanningData(planningRowRef.current?.data);
        const yk = effectiveForecastYearStr;
        const bucket = byYear[yk];

        if (bucket?.api && bucket?.table) {
            pendingInitialDataBucketRef.current = null;
            suppressApiTableMergeRef.current = true;
            dispatch(setForecastDemandPlanningData(bucket.api));
            setChannels(bucket.table.channels || []);
            setCategories(bucket.table.categories || []);
            setSubCategories(bucket.table.subCategories || []);
            setMonths(bucket.table.months || []);
            queueMicrotask(() => {
                suppressApiTableMergeRef.current = false;
            });
        } else {
            /* No full saved bucket — need catalog in Redux + merged rows, then silent persist writes `data[year]`. */
            pendingInitialDataBucketRef.current = { loadKey: persistKey };
            setChannels([]);
            setCategories([]);
            setSubCategories([]);
            setMonths([]);
            setHistory([]);
            historyRef.current = [];
            if (bucket?.api && savedApiSnapshotLooksComplete(bucket.api)) {
                dispatch(setForecastDemandPlanningData(bucket.api));
                /* Redux updates next render; merge effects can lag one frame — hydrate from saved api like full bucket restore. */
                const built = buildTableRowsFromForecastApiPayload(bucket.api, effectiveForecastYearStr);
                setChannels(built.channels);
                setCategories(built.categories);
                setSubCategories(built.subCategories);
                setMonths(built.months);
            }
        }

        dispatch(
            getForecastDemandPlanningData({
                id: planningId,
                forecastYear: effectiveForecastYearStr,
            })
        );
    }, [dispatch, planningId, effectiveForecastYearStr, savedBucketFingerprint, savedYearBucketDigest]);

    /**
     * When GET/thunk fills Redux but channel/category merge effects do not apply in time, tables stay empty.
     * Rebuild row state from the API payload already in Redux (same mapping as merge-from-empty).
     */
    useEffect(() => {
        if (suppressApiTableMergeRef.current) return;
        if (!planningId || !effectiveForecastYearStr) return;
        const d = forecastDemandPlanningData;
        if (!d || String(d.id) !== String(planningId) || !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)) {
            return;
        }
        if (!Array.isArray(d.channels) || d.channels.length === 0) return;
        const needRows =
            channels.length === 0 ||
            categories.length === 0 ||
            subCategories.length === 0 ||
            months.length === 0;
        if (!needRows) return;

        const mergeSig = [
            String(planningId),
            effectiveForecastYearStr,
            String(d.id),
            (d.channels || []).length,
            (d.categories || []).length,
            (d.subcategories || []).length,
            (d.months || []).length,
        ].join("|");
        if (mergedForecastPayloadSigRef.current === mergeSig) {
            return;
        }

        const built = buildTableRowsFromForecastApiPayload(d, effectiveForecastYearStr);
        mergedForecastPayloadSigRef.current = mergeSig;
        setChannels(built.channels);
        setCategories(built.categories);
        setSubCategories(built.subCategories);
        setMonths(built.months);
    }, [
        forecastDemandPlanningData,
        planningId,
        effectiveForecastYearStr,
        channels.length,
        categories.length,
        subCategories.length,
        months.length,
    ]);

    /**
     * `ensureCatalog` fetches the same GET payload as Redux for the + pickers (`catalogForAdd`). When the
     * primary path (Redux GET + merge) does not copy into local state (year mismatch, loadKey skip, etc.),
     * we still have a full API snapshot here — hydrate the main tables from it.
     */
    useEffect(() => {
        if (suppressApiTableMergeRef.current) return;
        if (!planningId || !effectiveForecastYearStr || !catalogForAdd) return;
        if (String(catalogForAdd.id) !== String(planningId)) return;
        if (String(catalogForAdd.forecastYear ?? "") !== String(effectiveForecastYearStr)) return;

        const needRows =
            channels.length === 0 ||
            categories.length === 0 ||
            subCategories.length === 0 ||
            months.length === 0;
        if (!needRows) return;
        if (!Array.isArray(catalogForAdd.channels) || catalogForAdd.channels.length === 0) return;

        const built = buildTableRowsFromForecastApiPayload(catalogForAdd, effectiveForecastYearStr);
        mergedForecastPayloadSigRef.current = [
            String(planningId),
            effectiveForecastYearStr,
            String(catalogForAdd.id),
            (catalogForAdd.channels || []).length,
            (catalogForAdd.categories || []).length,
            (catalogForAdd.subcategories || []).length,
            (catalogForAdd.months || []).length,
        ].join("|");
        dispatch(setForecastDemandPlanningData(catalogForAdd));
        setChannels(built.channels);
        setCategories(built.categories);
        setSubCategories(built.subCategories);
        setMonths(built.months);
    }, [
        catalogForAdd,
        planningId,
        effectiveForecastYearStr,
        channels.length,
        categories.length,
        subCategories.length,
        months.length,
        dispatch,
    ]);

    /** First visit to a year without a full saved bucket: write `data[year]` after GET + table merge (same shape as manual saves). */
    useEffect(() => {
        const pending = pendingInitialDataBucketRef.current;
        if (!pending || !planningId) return;
        const loadKey = `${planningId}|${effectiveForecastYearStr}`;
        if (pending.loadKey !== loadKey) return;
        if (!effectiveForecastYearStr) return;
        if (isLoadingForecastDemandPlanningData) return;

        const d = forecastDemandPlanningData;
        if (!d || String(d.id) !== String(planningId) || !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)) {
            return;
        }

        const apiCh = Array.isArray(d.channels) ? d.channels.length : 0;
        const apiCat = Array.isArray(d.categories) ? d.categories.length : 0;
        const apiSub = Array.isArray(d.subcategories) ? d.subcategories.length : 0;
        const apiMo = Array.isArray(d.months) ? d.months.length : 0;
        if (apiCh === 0 || apiCat === 0 || apiSub === 0 || apiMo === 0) {
            return;
        }

        const yk = effectiveForecastYearStr;
        const byYear = normalizePlanningData(planningRowRef.current?.data);
        const existing = byYear[yk];
        if (existing?.api && existing?.table) {
            pendingInitialDataBucketRef.current = null;
            return;
        }

        let apiJson;
        try {
            apiJson = JSON.parse(JSON.stringify(d));
        } catch {
            return;
        }
        const {
            channels: rowChannels,
            categories: rowCategories,
            subCategories: rowSubCategories,
            months: rowMonths,
        } = buildTableRowsFromForecastApiPayload(d, effectiveForecastYearStr);
        const enriched = enrichForecastTableSnapshotForDataColumn({
            channels: rowChannels,
            categories: rowCategories,
            subCategories: rowSubCategories,
            months: rowMonths,
            revenueTarget,
            forecastYearStr: effectiveForecastYearStr,
        });
        const bucket = {
            ...(byYear[yk] || {}),
            api: apiJson,
            table: {
                channels: structuredClone(enriched.channels),
                categories: structuredClone(enriched.categories),
                subCategories: structuredClone(enriched.subCategories),
                months: structuredClone(enriched.months),
            },
        };
        const nextData = { ...byYear, [yk]: bucket };

        pendingInitialDataBucketRef.current = null;
        void dispatch(updateForecastDemandPlanning({ id: planningId, data: nextData, silent: true }))
            .unwrap()
            .then((res) => {
                const item = res?.item;
                if (item) {
                    planningRowRef.current = item;
                    onServerRecordUpdated?.(item);
                }
                setChannels(structuredClone(bucket.table.channels));
                setCategories(structuredClone(bucket.table.categories));
                setSubCategories(structuredClone(bucket.table.subCategories));
                setMonths(structuredClone(bucket.table.months));
            })
            .catch(() => {
                pendingInitialDataBucketRef.current = { loadKey };
            });
    }, [
        isLoadingForecastDemandPlanningData,
        forecastDemandPlanningData,
        planningId,
        effectiveForecastYearStr,
        revenueTarget,
        dispatch,
        onServerRecordUpdated,
    ]);

    const effectiveForecastYear = useMemo(() => {
        if (!effectiveForecastYearStr) return NaN;
        const n = Number.parseInt(effectiveForecastYearStr, 10);
        return Number.isFinite(n) ? n : NaN;
    }, [effectiveForecastYearStr]);

    /** Calendar-based: past forecast years fully closed; current year through prior month; future years none. */
    const isForecastMonthClosed = useCallback(
        (monthIndex0to11) => {
            const fy = effectiveForecastYear;
            if (!Number.isFinite(fy) || monthIndex0to11 < 0 || monthIndex0to11 > 11) return false;
            const now = new Date();
            const cy = now.getFullYear();
            const cm = now.getMonth();
            if (fy < cy) return true;
            if (fy > cy) return false;
            return monthIndex0to11 < cm;
        },
        [effectiveForecastYear]
    );

    const channelLtmPercentByKey = useMemo(() => {
        const d = forecastDemandPlanningData;
        if (
            !d ||
            String(d.id) !== String(planningId) ||
            !effectiveForecastYearStr ||
            !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)
        ) {
            return null;
        }
        const map = {};
        for (const c of d.channels || []) {
            if (c?.key) map[c.key] = c.ltmPercentage;
        }
        return Object.keys(map).length ? map : null;
    }, [forecastDemandPlanningData, planningId, effectiveForecastYearStr]);

    const channelLtmFraction = useCallback(
        (channelName) => {
            if (!channelLtmPercentByKey) return null;
            const key =
                channelName === "Shopify"
                    ? "shopify"
                    : channelName === "Amazon"
                      ? "amazon"
                      : null;
            if (
                key &&
                channelLtmPercentByKey[key] != null &&
                Number.isFinite(Number(channelLtmPercentByKey[key]))
            ) {
                return Number(channelLtmPercentByKey[key]) / 100;
            }
            return null;
        },
        [channelLtmPercentByKey]
    );

    const channelSplitTotalLtmFraction = useMemo(() => {
        if (!channelLtmPercentByKey || Object.keys(channelLtmPercentByKey).length === 0) {
            return null;
        }
        return (
            Object.values(channelLtmPercentByKey).reduce(
                (sum, v) => sum + Number(v || 0),
                0
            ) / 100
        );
    }, [channelLtmPercentByKey]);

    const categoryLtmPercentByName = useMemo(() => {
        const d = forecastDemandPlanningData;
        if (
            !d ||
            String(d.id) !== String(planningId) ||
            !effectiveForecastYearStr ||
            !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)
        ) {
            return null;
        }
        const map = {};
        for (const c of d.categories || []) {
            if (c?.categoryName != null) map[c.categoryName] = c.ltmPercentage;
        }
        return Object.keys(map).length ? map : null;
    }, [forecastDemandPlanningData, planningId, effectiveForecastYearStr]);

    const categoryLtmFraction = useCallback(
        (categoryName) => {
            const row = categories.find((c) => c.category === categoryName);
            if (
                row?.ltmPercentage != null &&
                Number.isFinite(Number(row.ltmPercentage))
            ) {
                return Number(row.ltmPercentage) / 100;
            }
            if (!categoryLtmPercentByName) return null;
            if (
                categoryLtmPercentByName[categoryName] != null &&
                Number.isFinite(Number(categoryLtmPercentByName[categoryName]))
            ) {
                return Number(categoryLtmPercentByName[categoryName]) / 100;
            }
            return null;
        },
        [categoryLtmPercentByName, categories]
    );

    const getLastClosedDate = (forecastYear) => lastClosedDateLabelForForecastYear(forecastYear);

    const categorySplitTotalLtmFraction = useMemo(() => {
        if (!categories.length) {
            if (!categoryLtmPercentByName || Object.keys(categoryLtmPercentByName).length === 0) {
                return null;
            }
            return (
                Object.values(categoryLtmPercentByName).reduce((sum, v) => sum + Number(v || 0), 0) / 100
            );
        }
        if (!categoryLtmPercentByName) return null;
        let sum = 0;
        let hasValue = false;
        for (const cat of categories) {
            const raw =
                cat.ltmPercentage != null && Number.isFinite(Number(cat.ltmPercentage))
                    ? Number(cat.ltmPercentage)
                    : categoryLtmPercentByName?.[cat.category];
            if (raw != null && Number.isFinite(Number(raw))) {
                sum += Number(raw);
                hasValue = true;
            }
        }
        return hasValue ? sum / 100 : null;
    }, [categories, categoryLtmPercentByName]);

    const subcategoryLtmPercentById = useMemo(() => {
        const d = forecastDemandPlanningData;
        if (
            !d ||
            String(d.id) !== String(planningId) ||
            !effectiveForecastYearStr ||
            !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)
        ) {
            return null;
        }
        const map = {};
        for (const s of d.subcategories || []) {
            if (s?.subcategoryId != null) map[String(s.subcategoryId)] = s.ltmPercentage;
        }
        return Object.keys(map).length ? map : null;
    }, [forecastDemandPlanningData, planningId, effectiveForecastYearStr]);

    const subcategoryLtmFraction = useCallback(
        (subcategoryId) => {
            const k = String(subcategoryId);
            const row = subCategories.find((s) => String(s.id) === k);
            if (row?.ltmPercentage != null && Number.isFinite(Number(row.ltmPercentage))) {
                return Number(row.ltmPercentage) / 100;
            }
            if (!subcategoryLtmPercentById) return null;
            if (
                subcategoryLtmPercentById[k] != null &&
                Number.isFinite(Number(subcategoryLtmPercentById[k]))
            ) {
                return Number(subcategoryLtmPercentById[k]) / 100;
            }
            return null;
        },
        [subcategoryLtmPercentById, subCategories]
    );

    const subcategorySplitTotalLtmFraction = useMemo(() => {
        if (!subCategories.length) {
            if (!subcategoryLtmPercentById || Object.keys(subcategoryLtmPercentById).length === 0) {
                return null;
            }
            return (
                Object.values(subcategoryLtmPercentById).reduce((sum, v) => sum + Number(v || 0), 0) / 100
            );
        }
        if (!subcategoryLtmPercentById) return null;
        let sum = 0;
        let hasValue = false;
        for (const sc of subCategories) {
            const sid = String(sc.id);
            const raw =
                sc.ltmPercentage != null && Number.isFinite(Number(sc.ltmPercentage))
                    ? Number(sc.ltmPercentage)
                    : subcategoryLtmPercentById?.[sid];
            if (raw != null && Number.isFinite(Number(raw))) {
                sum += Number(raw);
                hasValue = true;
            }
        }
        return hasValue ? sum / 100 : null;
    }, [subCategories, subcategoryLtmPercentById]);

    useEffect(() => {
        if (suppressApiTableMergeRef.current) return;
        if (!effectiveForecastYearStr) return;
        const d = forecastDemandPlanningData;
        if (
            !d ||
            String(d.id) !== String(planningId) ||
            !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)
        ) {
            return;
        }
        if (!Array.isArray(d.channels)) return;
        if (d.channels.length === 0) {
            setChannels([]);
            return;
        }
        setChannels((prev) => {
            const prevByChannel = new Map(prev.map((c) => [c.channel, c]));
            return d.channels.map((api) => {
                const label = channelLabelFromApiKey(api.key);
                const prevRow = prevByChannel.get(label);
                return {
                    channel: label,
                    pct: prevRow?.pct ?? null,
                    channelKey: api.key,
                };
            });
        });
    }, [forecastDemandPlanningData, planningId, effectiveForecastYearStr]);

    useEffect(() => {
        if (suppressApiTableMergeRef.current) return;
        if (!effectiveForecastYearStr) return;
        const d = forecastDemandPlanningData;
        if (
            !d ||
            String(d.id) !== String(planningId) ||
            !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)
        ) {
            return;
        }
        if (!Array.isArray(d.categories)) return;
        if (d.categories.length === 0) {
            setCategories([]);
            return;
        }
        setCategories((prev) => {
            const prevByName = new Map(prev.map((c) => [c.category, c]));
            return d.categories.map((api) => {
                const prevRow = prevByName.get(api.categoryName);
                const apiLtm =
                    api.ltmPercentage != null && Number.isFinite(Number(api.ltmPercentage))
                        ? Number(api.ltmPercentage)
                        : null;
                return {
                    category: api.categoryName,
                    categoryId: prevRow?.categoryId ?? api.categoryId ?? null,
                    pct: prevRow?.pct ?? null,
                    actuals_2025: api.actuals,
                    ytd_actuals:
                        api.ytd_actuals != null && Number.isFinite(Number(api.ytd_actuals))
                            ? Number(api.ytd_actuals)
                            : 0,
                    last_closed_date: getLastClosedDate(effectiveForecastYearStr),
                    ltmPercentage:
                        prevRow?.ltmPercentage != null && Number.isFinite(Number(prevRow.ltmPercentage))
                            ? Number(prevRow.ltmPercentage)
                            : apiLtm,
                };
            });
        });
    }, [forecastDemandPlanningData, planningId, effectiveForecastYearStr]);

    useEffect(() => {
        if (suppressApiTableMergeRef.current) return;
        if (!effectiveForecastYearStr) return;
        const d = forecastDemandPlanningData;
        if (
            !d ||
            String(d.id) !== String(planningId) ||
            !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)
        ) {
            return;
        }
        if (!Array.isArray(d.subcategories)) return;
        if (d.subcategories.length === 0) {
            setSubCategories([]);
            return;
        }
        setSubCategories((prev) => {
            const prevById = new Map(prev.map((s) => [String(s.id), s]));
            return d.subcategories.map((api) => {
                const id = String(api.subcategoryId);
                const prevRow = prevById.get(id);
                const apiLtm =
                    api.ltmPercentage != null && Number.isFinite(Number(api.ltmPercentage))
                        ? Number(api.ltmPercentage)
                        : null;
                return {
                    id,
                    subcategoryId: api.subcategoryId,
                    sub_category: api.subcategoryName,
                    category: api.categoryName ?? "",
                    categoryId: prevRow?.categoryId ?? api.categoryId ?? null,
                    pct_of_channel: prevRow?.pct_of_channel ?? null,
                    actuals_2025: api.actuals,
                    ytd_actuals:
                        api.ytd_actuals != null && Number.isFinite(Number(api.ytd_actuals))
                            ? Number(api.ytd_actuals)
                            : 0,
                    last_closed_date: getLastClosedDate(effectiveForecastYearStr),
                    ltmPercentage:
                        prevRow?.ltmPercentage != null && Number.isFinite(Number(prevRow.ltmPercentage))
                            ? Number(prevRow.ltmPercentage)
                            : apiLtm,
                };
            });
        });
    }, [forecastDemandPlanningData, planningId, effectiveForecastYearStr]);

    useEffect(() => {
        if (suppressApiTableMergeRef.current) return;
        if (!effectiveForecastYearStr) return;
        const d = forecastDemandPlanningData;
        if (
            !d ||
            String(d.id) !== String(planningId) ||
            !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)
        ) {
            return;
        }
        if (!Array.isArray(d.months)) return;
        if (d.months.length === 0) {
            setMonths([]);
            return;
        }
        setMonths((prev) => {
            const prevByMonth = new Map(prev.map((m) => [m.month, m]));
            return d.months.map((api) => {
                const prevRow = prevByMonth.get(api.key);
                return {
                    month: api.key,
                    historical_pct:
                        api.historicalPercentage != null && Number.isFinite(Number(api.historicalPercentage))
                            ? Number(api.historicalPercentage) / 100
                            : null,
                    override_pct: prevRow?.override_pct ?? null,
                    actual_pct:
                        api.actualPercentage != null && Number.isFinite(Number(api.actualPercentage))
                            ? Number(api.actualPercentage) / 100
                            : null,
                    actual_revenue:
                        api.actualRevenue != null && Number.isFinite(Number(api.actualRevenue))
                            ? Number(api.actualRevenue)
                            : null,
                };
            });
        });
    }, [forecastDemandPlanningData, planningId, effectiveForecastYearStr]);

    const mergeLastSavedFromItem = useCallback((item) => {
        if (!item) return;
        lastSavedRef.current = {
            notes: item.notes ?? "",
            forecastYear: item.forecastYear != null ? String(item.forecastYear) : "",
            revenueTarget: Number(item.revenueTarget) || 0,
        };
    }, []);

    const persistNotesIfChanged = useCallback(
        async (nextNotes) => {
            if (!planningId) return;
            if (normNotes(nextNotes) === normNotes(lastSavedRef.current.notes)) return;
            try {
                const data = await dispatch(
                    updateForecastDemandPlanning({ id: planningId, notes: nextNotes })
                ).unwrap();
                const item = data?.item;
                mergeLastSavedFromItem(item);
                onServerRecordUpdated?.(item);
            } catch (err) {
                toast.error(
                    typeof err === "string" ? err : "Could not save notes.",
                    { position: "bottom-right" }
                );
            }
        },
        [planningId, dispatch, mergeLastSavedFromItem, onServerRecordUpdated]
    );

    const persistForecastYearIfChanged = useCallback(
        async (yearStr) => {
            if (!planningId) return;
            const next = yearStr != null && yearStr !== "" ? String(yearStr) : "";
            if (next === lastSavedRef.current.forecastYear) return;
            try {
                const data = await dispatch(
                    updateForecastDemandPlanning({
                        id: planningId,
                        forecastYear: next === "" ? null : next,
                    })
                ).unwrap();
                const item = data?.item;
                mergeLastSavedFromItem(item);
                onServerRecordUpdated?.(item);
            } catch (err) {
                toast.error(
                    typeof err === "string" ? err : "Could not save forecast year.",
                    { position: "bottom-right" }
                );
            }
        },
        [planningId, dispatch, mergeLastSavedFromItem, onServerRecordUpdated]
    );

    /** After undo, persist notes / forecast year / revenue if they differ from server (`lastSavedRef`). */
    const persistUndoSnapshotPlanningFields = useCallback(
        async (restore) => {
            if (!planningId || !restore) return;
            const saved = lastSavedRef.current;
            const patch = {};

            const restNotes = restore.meta?.notes ?? "";
            if (normNotes(restNotes) !== normNotes(saved.notes)) {
                patch.notes = restNotes;
            }

            const restYear =
                restore.meta?.forecast_year != null && restore.meta.forecast_year !== ""
                    ? String(restore.meta.forecast_year)
                    : "";
            const savedYear = saved.forecastYear ?? "";
            if (restYear !== savedYear) {
                patch.forecastYear = restYear === "" ? null : restYear;
            }

            const restRev = Number(restore.revenueTarget);
            const savedRev = Number(saved.revenueTarget);
            if (
                Number.isFinite(restRev) !== Number.isFinite(savedRev) ||
                (Number.isFinite(restRev) && Number.isFinite(savedRev) && restRev !== savedRev)
            ) {
                patch.revenueTarget = Number.isFinite(restRev) ? restRev : null;
            }

            if (Object.keys(patch).length === 0) return;

            try {
                const data = await dispatch(
                    updateForecastDemandPlanning({ id: planningId, ...patch })
                ).unwrap();
                const item = data?.item;
                mergeLastSavedFromItem(item);
                onServerRecordUpdated?.(item);
            } catch (err) {
                toast.error(
                    typeof err === "string" ? err : "Could not sync undone planning fields.",
                    { position: "bottom-right" }
                );
            }
        },
        [planningId, dispatch, mergeLastSavedFromItem, onServerRecordUpdated]
    );

    /** Merge edited table rows into `data[forecast_year]` (API snapshot + table clone incl. derived metrics). */
    const persistForecastTableToDataColumn = useCallback(
        async (tableSnapshot, opts) => {
            if (!planningId || !effectiveForecastYearStr) return false;
            const baseApi = opts?.apiData ?? forecastDemandPlanningData;
            if (
                !baseApi ||
                String(baseApi.id) !== String(planningId) ||
                String(baseApi.forecastYear) !== String(effectiveForecastYearStr)
            ) {
                return false;
            }
            const yk = String(effectiveForecastYearStr);
            const byYear = normalizePlanningData(planningRowRef.current?.data);
            let apiJson;
            try {
                apiJson = JSON.parse(JSON.stringify(baseApi));
            } catch {
                return false;
            }
            alignForecastApiJsonWithTableSnapshot(apiJson, tableSnapshot);
            const rev =
                opts?.revenueTarget !== undefined && opts?.revenueTarget !== null
                    ? Number(opts.revenueTarget)
                    : revenueTarget;
            const enriched = enrichForecastTableSnapshotForDataColumn({
                channels: tableSnapshot.channels,
                categories: tableSnapshot.categories,
                subCategories: tableSnapshot.subCategories,
                months: tableSnapshot.months,
                revenueTarget: Number.isFinite(rev) ? rev : 0,
                forecastYearStr: effectiveForecastYearStr,
            });
            const bucket = {
                ...(byYear[yk] || {}),
                api: apiJson,
                table: {
                    channels: structuredClone(enriched.channels),
                    categories: structuredClone(enriched.categories),
                    subCategories: structuredClone(enriched.subCategories),
                    months: structuredClone(enriched.months),
                },
            };
            const nextData = { ...byYear, [yk]: bucket };
            try {
                const res = await dispatch(
                    updateForecastDemandPlanning({ id: planningId, data: nextData, silent: true })
                ).unwrap();
                const item = res?.item;
                if (item) {
                    planningRowRef.current = item;
                    onServerRecordUpdated?.(item);
                }
                dispatch(setForecastDemandPlanningData(apiJson));
                /** Keep UI in sync with the enriched snapshot we just persisted (derived columns, rounding). */
                setChannels(structuredClone(bucket.table.channels));
                setCategories(structuredClone(bucket.table.categories));
                setSubCategories(structuredClone(bucket.table.subCategories));
                setMonths(structuredClone(bucket.table.months));
                return true;
            } catch {
                /* silent; explicit saves still toast */
                return false;
            }
        },
        [
            planningId,
            effectiveForecastYearStr,
            forecastDemandPlanningData,
            revenueTarget,
            dispatch,
            onServerRecordUpdated,
        ]
    );

    /**
     * History snapshot: merge live Redux API metrics into row clones so undo/persist can rebuild `data.api`
     * (ids, LTM, actuals, ytd). `withVisibleLtm` adds LTM from derived maps when needed (e.g. delete confirm).
     */
    const snapshot = (opts) => {
        const base = structuredClone({
            meta,
            revenueTarget,
            channels,
            categories,
            subCategories,
            months,
        });
        const d = forecastDemandPlanningData;
        const yearOk =
            d &&
            planningId != null &&
            String(d.id) === String(planningId) &&
            effectiveForecastYearStr &&
            forecastPayloadMatchesUiYear(d, effectiveForecastYearStr);

        if (yearOk) {
            const catByName = new Map((d.categories || []).map((c) => [c.categoryName, c]));
            base.categories = base.categories.map((row) => {
                const api = catByName.get(row.category);
                return {
                    ...row,
                    categoryId: row.categoryId ?? api?.categoryId ?? null,
                    ltmPercentage:
                        row.ltmPercentage != null && Number.isFinite(Number(row.ltmPercentage))
                            ? Number(row.ltmPercentage)
                            : api?.ltmPercentage != null && Number.isFinite(Number(api.ltmPercentage))
                              ? Number(api.ltmPercentage)
                              : null,
                    actuals_2025:
                        row.actuals_2025 != null && Number.isFinite(Number(row.actuals_2025))
                            ? Number(row.actuals_2025)
                            : api?.actuals != null
                              ? Number(api.actuals)
                              : row.actuals_2025,
                    ytd_actuals:
                        row.ytd_actuals != null && Number.isFinite(Number(row.ytd_actuals))
                            ? Number(row.ytd_actuals)
                            : api?.ytd_actuals != null && Number.isFinite(Number(api.ytd_actuals))
                              ? Number(api.ytd_actuals)
                              : 0,
                };
            });

            const subById = new Map((d.subcategories || []).map((s) => [String(s.subcategoryId), s]));
            base.subCategories = base.subCategories.map((row) => {
                const api = subById.get(String(row.id));
                const idNum = Number(row.subcategoryId ?? row.id);
                return {
                    ...row,
                    subcategoryId:
                        api?.subcategoryId != null
                            ? api.subcategoryId
                            : Number.isFinite(idNum)
                              ? idNum
                              : row.subcategoryId ?? row.id,
                    categoryId: row.categoryId ?? api?.categoryId ?? null,
                    ltmPercentage:
                        row.ltmPercentage != null && Number.isFinite(Number(row.ltmPercentage))
                            ? Number(row.ltmPercentage)
                            : api?.ltmPercentage != null && Number.isFinite(Number(api.ltmPercentage))
                              ? Number(api.ltmPercentage)
                              : null,
                    actuals_2025:
                        row.actuals_2025 != null && Number.isFinite(Number(row.actuals_2025))
                            ? Number(row.actuals_2025)
                            : api?.actuals != null
                              ? Number(api.actuals)
                              : row.actuals_2025,
                    ytd_actuals:
                        row.ytd_actuals != null && Number.isFinite(Number(row.ytd_actuals))
                            ? Number(row.ytd_actuals)
                            : api?.ytd_actuals != null && Number.isFinite(Number(api.ytd_actuals))
                              ? Number(api.ytd_actuals)
                              : 0,
                };
            });

            const chByLabel = new Map((d.channels || []).map((c) => [channelLabelFromApiKey(c.key), c]));
            base.channels = base.channels.map((row) => {
                const api = chByLabel.get(row.channel);
                return {
                    ...row,
                    channelKey: row.channelKey ?? api?.key ?? null,
                };
            });
        }

        if (opts?.withVisibleLtm) {
            if (categoryLtmPercentByName) {
                base.categories = base.categories.map((c) => {
                    const v = categoryLtmPercentByName[c.category];
                    return {
                        ...c,
                        ltmPercentage:
                            v != null && Number.isFinite(Number(v))
                                ? Number(v)
                                : c.ltmPercentage ?? null,
                    };
                });
            }
            if (subcategoryLtmPercentById) {
                base.subCategories = base.subCategories.map((sc) => {
                    const v = subcategoryLtmPercentById[String(sc.id)];
                    return {
                        ...sc,
                        ltmPercentage:
                            v != null && Number.isFinite(Number(v))
                                ? Number(v)
                                : sc.ltmPercentage ?? null,
                    };
                });
            }
        }
        return base;
    };

    const applySnapshot = useCallback((restore) => {
        if (!restore) return;
        const snap = structuredClone(restore);
        setMeta(snap.meta);
        setRevenueTargetState(snap.revenueTarget);
        setChannels(snap.channels);
        setCategories(snap.categories);
        setSubCategories(snap.subCategories);
        setMonths(snap.months);
    }, []);

    const budgetForecastId = `${planningId}-demandplan`;
    const budgetsForPlan = (items) => (Array.isArray(items) ? items : []).filter((budget) => (
        String(budget.forecast_id ?? budget.forecastId ?? '') === budgetForecastId
    ));

    const setRevenueTarget = (value) => {
        const next = Number(value);
        if (!Number.isFinite(next) || next === Number(revenueTarget)) return;
        setTargetError(null);
        setTargetResult(null);
        setPendingRevenueTarget({ from: Number(revenueTarget) || 0, to: next });
        setTargetBudgetNames(budgetsForPlan(budgetCards).map((budget) => budget.name).filter(Boolean));
        setTargetBudgetLookupError(null);
        setTargetBudgetLookupLoading(true);
        void dispatch(getBudgetCards()).unwrap()
            .then((response) => {
                const budgets = budgetsForPlan(response?.items || response);
                setTargetBudgetNames(budgets.map((budget) => budget.name).filter(Boolean));
            })
            .catch(() => {
                setTargetBudgetLookupError('Could not load linked Budget names.');
            })
            .finally(() => setTargetBudgetLookupLoading(false));
    };

    const cancelRevenueTargetChange = () => {
        if (targetSaving) return;
        setPendingRevenueTarget(null);
        setTargetBudgetLookupError(null);
    };

    const confirmRevenueTargetChange = async () => {
        if (!pendingRevenueTarget || !planningId || targetSaving) return;
        setTargetSaving(true);
        setTargetError(null);
        try {
            const result = await dispatch(updateForecastDemandPlanRevenueTarget({
                id: planningId,
                revenueTarget: pendingRevenueTarget.to,
                expectedUpdatedAt: selectedForecastDemandPlanning?.updatedAt
                    ?? selectedForecastDemandPlanning?.updated_at,
            })).unwrap();
            const item = result?.item;
            if (item) {
                historyRef.current = [];
                setHistory([]);
                const yearKey = String(item.forecastYear ?? effectiveForecastYearStr);
                const bucket = normalizePlanningData(item.data)[yearKey];
                setRevenueTargetState(Number(item.revenueTarget ?? result.target?.to) || 0);
                if (bucket?.table) {
                    setChannels(bucket.table.channels || []);
                    setCategories(bucket.table.categories || []);
                    setSubCategories(bucket.table.subCategories || []);
                    setMonths(bucket.table.months || []);
                }
                if (bucket?.api) dispatch(setForecastDemandPlanningData(bucket.api));
                planningRowRef.current = item;
                mergeLastSavedFromItem(item);
                onServerRecordUpdated?.(item);
            }
            setTargetResult(result);
            setPendingRevenueTarget(null);
        } catch (error) {
            setTargetError(typeof error === 'string' ? error : error?.error || error?.message || 'Could not update the Gross Sales Target.');
            setPendingRevenueTarget(null);
        } finally {
            setTargetSaving(false);
        }
    };

    const pushHistory = (opts) => {
        const snap = snapshot(opts);
        setHistory((prev) => {
            const next = [snap, ...prev].slice(0, MAX_HISTORY);
            historyRef.current = next;
            return next;
        });
    };

    const closeDeleteModal = useCallback(() => {
        setDeleteCategory(null);
        setDeleteSubCategory(null);
    }, []);

    const submitDeleteModal = useCallback(async () => {
        if (!forecastDemandPlanningData || !planningId) {
            closeDeleteModal();
            return;
        }
        if (deleteCategory) {
            pushHistory({ withVisibleLtm: true });
            const categoryName = deleteCategory.category;
            const nextCategories = categories.filter((c) => c.category !== categoryName);
            const nextSub = subCategories.filter((sc) => sc.category !== categoryName);
            const nextApi = {
                ...forecastDemandPlanningData,
                categories: (forecastDemandPlanningData.categories || []).filter(
                    (c) => c.categoryName !== categoryName
                ),
                subcategories: (forecastDemandPlanningData.subcategories || []).filter(
                    (s) => (s.categoryName ?? "") !== categoryName
                ),
            };
            dispatch(setForecastDemandPlanningData(nextApi));
            setCategories(nextCategories);
            setSubCategories(nextSub);
            closeDeleteModal();
            setDeleteSaving(true);
            try {
                const ok = await persistForecastTableToDataColumn(
                    { channels, categories: nextCategories, subCategories: nextSub, months },
                    { apiData: nextApi }
                );
                if (ok) {
                    toast.success(`Removed category "${categoryName}" for ${meta?.forecast_year}.`, {
                        position: "bottom-right",
                    });
                } else {
                    toast.error("Could not save removal.", { position: "bottom-right" });
                }
            } finally {
                setDeleteSaving(false);
            }
            return;
        }
        if (deleteSubCategory) {
            pushHistory({ withVisibleLtm: true });
            const subLabel = deleteSubCategory.sub_category;
            const sid = String(deleteSubCategory.id);
            const nextSub = subCategories.filter((sc) => sc.id !== sid);
            const nextApi = {
                ...forecastDemandPlanningData,
                subcategories: (forecastDemandPlanningData.subcategories || []).filter(
                    (s) => String(s.subcategoryId) !== sid
                ),
            };
            dispatch(setForecastDemandPlanningData(nextApi));
            setSubCategories(nextSub);
            closeDeleteModal();
            setDeleteSaving(true);
            try {
                const ok = await persistForecastTableToDataColumn(
                    { channels, categories, subCategories: nextSub, months },
                    { apiData: nextApi }
                );
                if (ok) {
                    toast.success(`Removed sub-category "${subLabel}" for ${meta?.forecast_year}.`, {
                        position: "bottom-right",
                    });
                } else {
                    toast.error("Could not save removal.", { position: "bottom-right" });
                }
            } finally {
                setDeleteSaving(false);
            }
            return;
        }
        closeDeleteModal();
    }, [
        forecastDemandPlanningData,
        planningId,
        deleteCategory,
        deleteSubCategory,
        categories,
        subCategories,
        channels,
        months,
        dispatch,
        persistForecastTableToDataColumn,
        closeDeleteModal,
        effectiveForecastYearStr,
        pushHistory,
    ]);

    const ensureCatalog = useCallback(async () => {
        if (!planningId || !effectiveForecastYearStr) return;
        const key = `${planningId}|${effectiveForecastYearStr}`;
        if (catalogKeyRef.current === key) return;

        const fromRedux = forecastDemandPlanningData;
        if (
            fromRedux &&
            String(fromRedux.id) === String(planningId) &&
            forecastPayloadMatchesUiYear(fromRedux, effectiveForecastYearStr) &&
            savedApiSnapshotLooksComplete(fromRedux)
        ) {
            setCatalogForAdd(fromRedux);
            catalogKeyRef.current = key;
            return;
        }

        setCatalogLoading(true);
        try {
            const data = await dispatch(
                getForecastDemandPlanningData({
                    id: planningId,
                    forecastYear: effectiveForecastYearStr,
                })
            ).unwrap();
            setCatalogForAdd(data);
            catalogKeyRef.current = key;
        } catch (e) {
            console.log(e);
        } finally {
            setCatalogLoading(false);
        }
    }, [dispatch, effectiveForecastYearStr, forecastDemandPlanningData, planningId]);

    const ensureFullCatalogForAdd = useCallback(async () => {
        if (!planningId || !effectiveForecastYearStr) return;
        const key = `${planningId}|${effectiveForecastYearStr}`;
        if (fullCatalogKeyRef.current === key) return;
        try {
            const { data } = await axios.get("/api/v1/forecast-demand-planning-data", {
                params: { id: planningId, forecastYear: effectiveForecastYearStr, useSavedApi: 0 },
            });
            setFullCatalogForAdd(data);
            fullCatalogKeyRef.current = key;
        } catch (e){
            console.log(e)
        }
    }, [planningId, effectiveForecastYearStr]);

    const ensureMasterCatalogForAdd = useCallback(async () => {
        if (masterCatalogLoadedRef.current) return;
        try {
            const { data } = await axios.get("/api/v1/product-catalog/settings/categories");
            const groups = Array.isArray(data?.items) ? data.items : [];
            const categoriesMaster = groups.map((g) => ({
                categoryId: g.id,
                categoryName: g.name,
                ltmPercentage: 0,
                actuals: 0,
                ytd_actuals: 0,
            }));
            const subcategoriesMaster = groups.flatMap((g) =>
                (g.variants || []).map((v) => ({
                    subcategoryId: v.id,
                    subcategoryName: v.name,
                    categoryId: g.id,
                    categoryName: g.name,
                    ltmPercentage: 0,
                    actuals: 0,
                    ytd_actuals: 0,
                }))
            );
            setMasterCatalogForAdd({
                categories: categoriesMaster,
                subcategories: subcategoriesMaster,
            });
            masterCatalogLoadedRef.current = true;
        } catch (e) {
            const msg =
                typeof e?.response?.data === "string"
                    ? e.response.data
                    : e?.message || "Could not load product category settings.";
            toast.error(msg, { position: "bottom-right" });
        }
    }, []);

    /** Lazy-load add-menu catalogs when + pickers open (not on page mount — avoids duplicate GET with main catalog load).
     *  Same-endpoint GETs run sequentially: global axios cancel shares `get:/api/v1/forecast-demand-planning-data`
     *  and would otherwise abort each other (saved catalog thunk vs `useSavedApi: 0` full metrics). */
    useEffect(() => {
        if (categoryAddOpen || subcategoryAddOpen) {
            void (async () => {
                await ensureCatalog();
                await ensureFullCatalogForAdd();
            })();
            void ensureMasterCatalogForAdd();
        }
    }, [
        categoryAddOpen,
        subcategoryAddOpen,
        ensureCatalog,
        ensureFullCatalogForAdd,
        ensureMasterCatalogForAdd,
    ]);

    const catalogCategoriesUniverse = useMemo(() => {
        const master = Array.isArray(masterCatalogForAdd?.categories)
            ? masterCatalogForAdd.categories
            : [];
        const forecast = Array.isArray(fullCatalogForAdd?.categories)
            ? fullCatalogForAdd.categories
            : Array.isArray(catalogForAdd?.categories)
            ? catalogForAdd.categories
            : [];
        if (!master.length) return forecast;

        const byId = new Map(
            forecast
                .filter((c) => c?.categoryId != null)
                .map((c) => [String(c.categoryId), c])
        );
        const byName = new Map(
            forecast
                .filter((c) => c?.categoryName != null)
                .map((c) => [String(c.categoryName).trim().toLowerCase(), c])
        );
        return master.map((m) => {
            const fromForecast =
                byId.get(String(m.categoryId)) ||
                byName.get(String(m.categoryName || "").trim().toLowerCase());
            return fromForecast
                ? { ...m, ...fromForecast, categoryId: m.categoryId, categoryName: m.categoryName }
                : m;
        });
    }, [masterCatalogForAdd, fullCatalogForAdd, catalogForAdd]);

    const catalogSubcategoriesUniverse = useMemo(() => {
        const master = Array.isArray(masterCatalogForAdd?.subcategories)
            ? masterCatalogForAdd.subcategories
            : [];
        const forecast = Array.isArray(fullCatalogForAdd?.subcategories)
            ? fullCatalogForAdd.subcategories
            : Array.isArray(catalogForAdd?.subcategories)
            ? catalogForAdd.subcategories
            : [];
        if (!master.length) return forecast;

        const byId = new Map(
            forecast
                .filter((s) => s?.subcategoryId != null)
                .map((s) => [String(s.subcategoryId), s])
        );
        const byName = new Map(
            forecast
                .filter((s) => s?.subcategoryName != null && s?.categoryName != null)
                .map((s) => [
                    `${String(s.categoryName).trim().toLowerCase()}|${String(s.subcategoryName)
                        .trim()
                        .toLowerCase()}`,
                    s,
                ])
        );
        return master.map((m) => {
            const compositeKey = `${String(m.categoryName || "").trim().toLowerCase()}|${String(
                m.subcategoryName || ""
            )
                .trim()
                .toLowerCase()}`;
            const fromForecast = byId.get(String(m.subcategoryId)) || byName.get(compositeKey);
            return fromForecast
                ? {
                      ...m,
                      ...fromForecast,
                      subcategoryId: m.subcategoryId,
                      subcategoryName: m.subcategoryName,
                      categoryId: m.categoryId,
                      categoryName: m.categoryName,
                  }
                : m;
        });
    }, [masterCatalogForAdd, fullCatalogForAdd, catalogForAdd]);

    const availableCategoriesToAdd = useMemo(() => {
        if (!catalogCategoriesUniverse.length) return [];
        const taken = new Set(categories.map((c) => c.category));
        const q = categoryAddQuery.trim().toLowerCase();
        return catalogCategoriesUniverse.filter((c) => {
            if (taken.has(c.categoryName)) return false;
            if (!q) return true;
            return String(c.categoryName || "").toLowerCase().includes(q);
        });
    }, [catalogCategoriesUniverse, categories, categoryAddQuery]);

    const availableSubcategoriesToAdd = useMemo(() => {
        if (!catalogSubcategoriesUniverse.length) return [];
        const taken = new Set(subCategories.map((s) => String(s.id)));
        const q = subcategoryAddQuery.trim().toLowerCase();
        return catalogSubcategoriesUniverse.filter((s) => {
            if (taken.has(String(s.subcategoryId))) return false;
            if (!q) return true;
            const hay = `${s.subcategoryName || ""} ${s.categoryName || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [catalogSubcategoriesUniverse, subCategories, subcategoryAddQuery]);

    /** False until catalog is loaded; then true only if at least one catalog row is not already in the table. */
    const hasRemainingCategoriesToAdd = useMemo(() => {
        if (!catalogCategoriesUniverse.length) return false;
        const taken = new Set(categories.map((c) => c.category));
        return catalogCategoriesUniverse.some((c) => !taken.has(c.categoryName));
    }, [catalogCategoriesUniverse, categories]);

    const hasRemainingSubcategoriesToAdd = useMemo(() => {
        if (!catalogSubcategoriesUniverse.length) return false;
        const taken = new Set(subCategories.map((s) => String(s.id)));
        return catalogSubcategoriesUniverse.some((s) => !taken.has(String(s.subcategoryId)));
    }, [catalogSubcategoriesUniverse, subCategories]);

    const showCategoryAddButton = hasRemainingCategoriesToAdd;
    const showSubcategoryAddButton = hasRemainingSubcategoriesToAdd;

    const addPickedCategory = useCallback(
        (apiCat) => {
            if (!forecastDemandPlanningData || !apiCat?.categoryName) return;
            if (categories.some((c) => c.category === apiCat.categoryName)) return;
            pushHistory();
            const nextCategoriesApi = [...(forecastDemandPlanningData.categories || []), apiCat].sort((a, b) =>
                String(a.categoryName || "").localeCompare(String(b.categoryName || ""))
            );
            const nextApi = { ...forecastDemandPlanningData, categories: nextCategoriesApi };
            const prevByName = new Map(categories.map((c) => [c.category, c]));
            const nextCategoryRows = nextCategoriesApi.map((api) => ({
                category: api.categoryName,
                pct: prevByName.get(api.categoryName)?.pct ?? null,
                actuals_2025: api.actuals,
                ytd_actuals:
                    api.ytd_actuals != null && Number.isFinite(Number(api.ytd_actuals))
                        ? Number(api.ytd_actuals)
                        : 0,
                last_closed_date: getLastClosedDate(effectiveForecastYearStr),
            }));
            dispatch(setForecastDemandPlanningData(nextApi));
            setCategories(nextCategoryRows);
            setCategoryAddOpen(false);
            setCategoryAddQuery("");
            void persistForecastTableToDataColumn(
                { channels, categories: nextCategoryRows, subCategories, months },
                { apiData: nextApi }
            );
        },
        [
            forecastDemandPlanningData,
            categories,
            channels,
            subCategories,
            months,
            dispatch,
            pushHistory,
            persistForecastTableToDataColumn,
            effectiveForecastYearStr,
        ]
    );

    const addPickedSubcategory = useCallback(
        (apiSub) => {
            if (!forecastDemandPlanningData || apiSub?.subcategoryId == null) return;
            const sid = String(apiSub.subcategoryId);
            if (subCategories.some((s) => s.id === sid)) return;
            pushHistory();
            const nextSubsApi = [...(forecastDemandPlanningData.subcategories || []), apiSub].sort((a, b) => {
                const ca = String(a.categoryName || "").localeCompare(String(b.categoryName || ""));
                if (ca !== 0) return ca;
                return String(a.subcategoryName || "").localeCompare(String(b.subcategoryName || ""));
            });
            const nextApi = { ...forecastDemandPlanningData, subcategories: nextSubsApi };
            const prevById = new Map(subCategories.map((s) => [String(s.id), s]));
            const nextSubRows = nextSubsApi.map((api) => ({
                id: String(api.subcategoryId),
                sub_category: api.subcategoryName,
                category: api.categoryName ?? "",
                pct_of_channel: prevById.get(String(api.subcategoryId))?.pct_of_channel ?? null,
                actuals_2025: api.actuals,
                ytd_actuals:
                    api.ytd_actuals != null && Number.isFinite(Number(api.ytd_actuals))
                        ? Number(api.ytd_actuals)
                        : 0,
                last_closed_date: getLastClosedDate(effectiveForecastYearStr),
            }));
            dispatch(setForecastDemandPlanningData(nextApi));
            setSubCategories(nextSubRows);
            setSubcategoryAddOpen(false);
            setSubcategoryAddQuery("");
            void persistForecastTableToDataColumn(
                { channels, categories, subCategories: nextSubRows, months },
                { apiData: nextApi }
            );
        },
        [
            forecastDemandPlanningData,
            categories,
            channels,
            subCategories,
            months,
            dispatch,
            pushHistory,
            persistForecastTableToDataColumn,
            effectiveForecastYearStr,
        ]
    );

    const toggleCategoryAdd = useCallback(() => {
        setSubcategoryAddOpen(false);
        setCategoryAddOpen((o) => !o);
    }, []);

    const toggleSubcategoryAdd = useCallback(() => {
        setCategoryAddOpen(false);
        setSubcategoryAddOpen((o) => !o);
    }, []);

    const updateCategoryDropdownPosition = useCallback(() => {
        const btn = categoryAddAnchorRef.current;
        if (!btn) return;
        const r = btn.getBoundingClientRect();
        const left = computeAddDropdownLeft(r, FORECAST_ADD_CATEGORY_PANEL_W, marginFromSidebar || 0);
        setCategoryDropdownPos({ top: r.bottom + 4, left });
    }, [marginFromSidebar]);

    const updateSubcategoryDropdownPosition = useCallback(() => {
        const btn = subcategoryAddAnchorRef.current;
        if (!btn) return;
        const r = btn.getBoundingClientRect();
        const left = computeAddDropdownLeft(r, FORECAST_ADD_SUBCATEGORY_PANEL_W, marginFromSidebar || 0);
        setSubcategoryDropdownPos({ top: r.bottom + 4, left });
    }, [marginFromSidebar]);

    useLayoutEffect(() => {
        if (!categoryAddOpen) return;
        updateCategoryDropdownPosition();
        window.addEventListener("resize", updateCategoryDropdownPosition);
        window.addEventListener("scroll", updateCategoryDropdownPosition, true);
        return () => {
            window.removeEventListener("resize", updateCategoryDropdownPosition);
            window.removeEventListener("scroll", updateCategoryDropdownPosition, true);
        };
    }, [categoryAddOpen, updateCategoryDropdownPosition]);

    useLayoutEffect(() => {
        if (!subcategoryAddOpen) return;
        updateSubcategoryDropdownPosition();
        window.addEventListener("resize", updateSubcategoryDropdownPosition);
        window.addEventListener("scroll", updateSubcategoryDropdownPosition, true);
        return () => {
            window.removeEventListener("resize", updateSubcategoryDropdownPosition);
            window.removeEventListener("scroll", updateSubcategoryDropdownPosition, true);
        };
    }, [subcategoryAddOpen, updateSubcategoryDropdownPosition]);

    useEffect(() => {
        if (!showCategoryAddButton && categoryAddOpen) setCategoryAddOpen(false);
    }, [showCategoryAddButton, categoryAddOpen]);

    useEffect(() => {
        if (!showSubcategoryAddButton && subcategoryAddOpen) setSubcategoryAddOpen(false);
    }, [showSubcategoryAddButton, subcategoryAddOpen]);

    useEffect(() => {
        if (!categoryAddOpen && !subcategoryAddOpen) return;
        const onPointerDown = (e) => {
            const t = e.target;
            if (
                categoryAddAnchorRef.current?.contains(t) ||
                categoryAddPortalRef.current?.contains(t) ||
                subcategoryAddAnchorRef.current?.contains(t) ||
                subcategoryAddPortalRef.current?.contains(t)
            ) {
                return;
            }
            setCategoryAddOpen(false);
            setSubcategoryAddOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [categoryAddOpen, subcategoryAddOpen]);

    useEffect(() => {
        if (!categoryAddOpen && !subcategoryAddOpen) return;
        const onEsc = (e) => {
            if (e.key === "Escape") {
                setCategoryAddOpen(false);
                setSubcategoryAddOpen(false);
            }
        };
        window.addEventListener("keydown", onEsc);
        return () => window.removeEventListener("keydown", onEsc);
    }, [categoryAddOpen, subcategoryAddOpen]);

    const undo = useCallback(() => {
        const prev = historyRef.current;
        if (!prev.length) return;
        const [restore, ...rest] = prev;
        historyRef.current = rest;
        setHistory(rest);

        suppressApiTableMergeRef.current = true;
        applySnapshot(restore);
        void (async () => {
            try {
                await persistForecastTableToDataColumn(
                    {
                        channels: restore.channels,
                        categories: restore.categories,
                        subCategories: restore.subCategories,
                        months: restore.months,
                    },
                    { revenueTarget: restore.revenueTarget }
                );
                await persistUndoSnapshotPlanningFields(restore);
            } finally {
                queueMicrotask(() => {
                    suppressApiTableMergeRef.current = false;
                });
            }
        })();
        toast.success("Undone", { autoClose: 1500, position: "bottom-right" });
    }, [applySnapshot, persistForecastTableToDataColumn, persistUndoSnapshotPlanningFields]);

    const canUndo = history.length > 0;

    useEffect(() => {
        const onKeyDown = (e) => {
            if (!(e.ctrlKey || e.metaKey) || e.key !== "z" || e.shiftKey) return;
            const el = e.target;
            const tag = el?.tagName?.toLowerCase?.();
            if (tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable)
                return;
            e.preventDefault();
            undo();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [undo]);

    const updateMeta = (patch) => {
        pushHistory();
        setMeta((prev) => ({ ...prev, ...patch }));
    };

    const updateCategoryPct = (category, pct) => {
        pushHistory();
        const nextCategories = categories.map((c) => (c.category === category ? { ...c, pct } : c));
        setCategories(nextCategories);
        void persistForecastTableToDataColumn({
            channels,
            categories: nextCategories,
            subCategories,
            months,
        });
    };

    const updateChannelPct = (channel, pct) => {
        pushHistory();
        const nextChannels = channels.map((c) => (c.channel === channel ? { ...c, pct } : c));
        setChannels(nextChannels);
        void persistForecastTableToDataColumn({
            channels: nextChannels,
            categories,
            subCategories,
            months,
        });
    };

    const updateSubCategoryPct = (id, pct) => {
        pushHistory();
        const nextSubCategories = subCategories.map((sc) =>
            sc.id === id ? { ...sc, pct_of_channel: pct } : sc
        );
        setSubCategories(nextSubCategories);
        void persistForecastTableToDataColumn({
            channels,
            categories,
            subCategories: nextSubCategories,
            months,
        });
    };

    const updateMonthOverride = (month, pct) => {
        pushHistory();
        const nextMonths = months.map((m) => (m.month === month ? { ...m, override_pct: pct } : m));
        setMonths(nextMonths);
        void persistForecastTableToDataColumn({
            channels,
            categories,
            subCategories,
            months: nextMonths,
        });
    };

    const channelPctTotal = channels.reduce((s, c) => s + (c.pct ?? 0), 0);
    const channelValid = Math.abs(channelPctTotal - 1) < 0.0001;

    const catPctTotal = categories.reduce((s, c) => s + (c.pct ?? 0), 0);
    const catValid = Math.abs(catPctTotal - 1) < 0.0001;

    const subCatPctTotal = subCategories.reduce((s, sc) => s + (sc.pct_of_channel ?? 0), 0);
    const subCatValid = Math.abs(subCatPctTotal - 1) < 0.0001;

    /** Same rule as Category %: editable Override % values must sum to 100% (null → 0). */
    const monthOverridePctTotal = months.reduce((s, m) => s + (m.override_pct ?? 0), 0);
    const monthOverridePctTotalRounded = Number(monthOverridePctTotal.toFixed(4));
    const monthValid = Math.abs(monthOverridePctTotalRounded - 1) < 0.0001;

    const ytdForecastTotal = months.reduce(
        (sum, m, idx) => (isForecastMonthClosed(idx) ? sum + monthlyTarget(revenueTarget, m) : sum),
        0
    );
    const catYtdForecast = (pct) => Math.round(ytdForecastTotal * pct);
    const categoryRevTargetFor = (categoryName) => {
        const catPct = categories.find((c) => c.category === categoryName)?.pct ?? 0;
        return categoryTarget(revenueTarget, catPct);
    };
    const subCatYtdForecast = (categoryName, poc) => {
        const catPct = categories.find((c) => c.category === categoryName)?.pct ?? 0;
        return Math.round(ytdForecastTotal * catPct * poc);
    };
    const catPctCheck = (category) =>
        subCategories
            .filter((sc) => sc.category === category)
            .reduce((s, sc) => s + (sc.pct_of_channel ?? 0), 0);

    const uiForecastCatalogReady =
        channels.length > 0 &&
        categories.length > 0 &&
        subCategories.length > 0 &&
        months.length > 0;

    const forecastCatalogMatches = useMemo(() => {
        const d = forecastDemandPlanningData;
        if (
            !d ||
            planningId == null ||
            String(d.id) !== String(planningId) ||
            !effectiveForecastYearStr ||
            !forecastPayloadMatchesUiYear(d, effectiveForecastYearStr)
        ) {
            return false;
        }
        const ch = Array.isArray(d.channels) ? d.channels.length : 0;
        const cat = Array.isArray(d.categories) ? d.categories.length : 0;
        const sub = Array.isArray(d.subcategories) ? d.subcategories.length : 0;
        const mo = Array.isArray(d.months) ? d.months.length : 0;
        return ch > 0 && cat > 0 && sub > 0 && mo > 0;
    }, [forecastDemandPlanningData, planningId, effectiveForecastYearStr]);

    const retryForecastCatalogLoad = useCallback(() => {
        if (!planningId || !effectiveForecastYearStr) return;
        lastForecastLoadKeyRef.current = "";
        dispatch(
            getForecastDemandPlanningData({
                id: planningId,
                forecastYear: effectiveForecastYearStr,
            })
        );
    }, [dispatch, planningId, effectiveForecastYearStr]);

    const showCatalogLoadError = useMemo(() => {
        if (!planningId || !effectiveForecastYearStr) return false;
        if (isLoadingForecastDemandPlanningData) return false;
        if (forecastCatalogMatches) return false;
        return Boolean(forecastCatalogGetError);
    }, [
        planningId,
        effectiveForecastYearStr,
        isLoadingForecastDemandPlanningData,
        forecastCatalogMatches,
        forecastCatalogGetError,
    ]);

    /** Fetch in flight, or catalog API not ready for the selected planning year. */
    const showCatalogTablesLoader = useMemo(() => {
        if (!planningId || !effectiveForecastYearStr) return false;
        if (showCatalogLoadError) return false;
        if (forecastCatalogMatches && uiForecastCatalogReady) return false;
        if (isLoadingForecastDemandPlanningData) return true;
        if (!forecastCatalogMatches) return true;
        if (!uiForecastCatalogReady) return true;
        return false;
    }, [
        planningId,
        effectiveForecastYearStr,
        showCatalogLoadError,
        forecastCatalogMatches,
        uiForecastCatalogReady,
        isLoadingForecastDemandPlanningData,
    ]);

    const CompactField = ({ label, value }) => (
        <div className="min-w-0">
            <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                {label}
            </p>
    
            <div className="flex h-[28px] items-center rounded-[4px] border border-gray-200 bg-gray-50 px-2 text-[12px] text-gray-900">
                <span className="truncate">{value}</span>
            </div>
        </div>
    );

    const tablesLoading = showCatalogTablesLoader;

    const catalogLoadErrorBanner = showCatalogLoadError ? (
        <div className="mb-4 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2 min-w-0">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
                <div className="min-w-0">
                    <p className="text-[12px] font-medium text-amber-900">
                        LTM allocation data could not be loaded
                    </p>
                    <p className="text-[11px] text-amber-800/90">
                        {typeof forecastCatalogGetError === "string"
                            ? forecastCatalogGetError
                            : "The forecast catalog request timed out or failed. LTM % is hidden until data loads."}
                    </p>
                </div>
            </div>
            <button
                type="button"
                className="shrink-0 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-[12px] font-medium text-amber-900 hover:bg-amber-100"
                onClick={retryForecastCatalogLoad}
            >
                Retry load
            </button>
        </div>
    ) : null;

    const catalogTablesBody = (tableContent) => {
        if (tablesLoading) {
            return (
                <div className="flex min-h-[168px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/70 px-4 py-14">
                    <LoaderSpinner width="40" height="40" border="3" colorSpinner="gray" />
                </div>
            );
        }
        if (showCatalogLoadError && !uiForecastCatalogReady) {
            return (
                <div className="flex min-h-[168px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-amber-200 bg-amber-50/70 px-4 py-10">
                    {catalogLoadErrorBanner}
                </div>
            );
        }
        return (
            <>
                {showCatalogLoadError ? catalogLoadErrorBanner : null}
                {tableContent}
            </>
        );
    };

    return (
        <>
            <section className="overflow-hidden rounded-lg bg-white border border-gray-200 shadow-sm relative">
                <div className="flex h-full flex-col overflow-hidden">
                    <div className="flex items-center gap-2 justify-between border-b border-border bg-background px-4 py-2 xl:px-6">
                        <div>
                            <h1 className="text-[14px] text-primary font-semibold leading-tight">
                                Forecast Demand
                            </h1>
                            <p className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
                                <span>{selectedForecastDemandPlanning?.name || "N/A"}</span>
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${selectedForecastDemandPlanning?.publishStatus === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}>
                                    {selectedForecastDemandPlanning?.publishStatus === 'PUBLISHED' ? 'Published' : 'Draft'}{(forecastDemandPlanningData?.gross_basis || selectedForecastDemandPlanning?.gross_basis) === 'true_gross' ? ' · True Gross Sales' : ''}
                                </span>
                            </p>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-1.5 text-[11px]">
                                <span className="inline-block h-2.5 w-2.5 rounded-[2px] border border-[#c5d8fc] bg-[#e8f0fe]" />
                                <span>Manual Input</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-[11px]">
                                <span className="inline-block h-2.5 w-2.5 rounded-[2px] border border-[#dad6d7] bg-[#f3f1f2]" />
                                <span>Derived</span>
                            </div>
                            <div className="w-[1px] h-4 bg-gray-200"/>
                            <button
                                type="button"
                                onClick={() => undo()}
                                disabled={!canUndo}
                                title={canUndo ? "Undo last change (Ctrl+Z)" : "Nothing to undo"}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    padding: "4px 10px",
                                    borderRadius: 5,
                                    border: "1px solid #e5e7eb",
                                    background: canUndo ? "#1f2937" : "#f3f4f6",
                                    color: canUndo ? "#ffffff" : "#9ca3af",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    cursor: canUndo ? "pointer" : "default",
                                    transition: "background 0.15s, color 0.15s",
                                }}
                            >
                                <Undo2 size={12} />
                                Undo
                            </button>
                        </div>
                    </div>
                </div>
                <div className="flex-1 space-y-4 overflow-y-auto px-3 py-3 xl:px-6 xl:py-5">
                    {/* ── SECTION 1 — Forecast Metadata ── */}
                    <SectionForecastDemand number="1" title="Forecast Metadata">
                        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 xl:grid-cols-3 xl:mr-[40%]">
                            <CompactField
                                label="Forecast Name"
                                value={selectedForecastDemandPlanning?.name || 'N/A'}
                            />
                            <CompactField
                                label="Created By"
                                value={selectedForecastDemandPlanning?.userName || 'N/A'}
                            />
                            <CompactField
                                label="Created Date"
                                value={formatForecastDemandPlanningCreatedDisplay(
                                    selectedForecastDemandPlanning
                                )}
                            />
                            <div>
                                <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                                    Forecast Year
                                </p>
                                <SelectDropdown
                                    options={getYearsFromToCurrent(2024)}
                                    value={meta?.forecast_year}
                                    portal={true}
                                    placeholder="Select Forecast Year"
                                    selectedValueClassname="!text-[12px] !text-gray-900"
                                    itemValueClassname="!py-1 !px-2 !text-[12px] !text-gray-900"
                                    selectClassname="
                                        !h-[28px]
                                        !px-2
                                        !bg-[#E8F0FE]
                                        !border
                                        !border-[#c5d8fc]
                                        hover:!bg-[#d6e6fd]
                                        hover:!border-[#a8c4fb]
                                        !rounded-[4px]
                                        !text-[12px]
                                        !text-gray-900
                                    "
                                    onChange={(v) => {
                                        const y = parseInt(v, 10);

                                        const yearStr = Number.isFinite(y)
                                            ? String(y)
                                            : meta.forecast_year;

                                        updateMeta({ forecast_year: yearStr });

                                        void persistForecastYearIfChanged(yearStr);
                                    }}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <MetaFieldForecastDemand
                                    label="Notes"
                                    value={meta?.notes}
                                    onChange={(v) => {
                                        updateMeta({ notes: v });
                                        void persistNotesIfChanged(v);
                                    }}
                                    wide
                                />
                            </div>
                        </div>
                    </SectionForecastDemand>
                    {/* ── SECTION 2 — Revenue Target ── */}
                    <SectionForecastDemand number="2" title={`Gross Sales Target${(forecastDemandPlanningData?.gross_basis || selectedForecastDemandPlanning?.gross_basis) === 'true_gross' ? ' · True Gross Sales' : ''}`}>
                        <div className="flex items-start gap-8">
                            <div>
                                <p
                                    className="text-[11px] text-gray-500 uppercase tracking-wide font-medium mb-1.5"
                                >
                                    Gross Sales Target
                                </p>
                                <RevenueInputForecastDemand value={revenueTarget} onSave={setRevenueTarget} />
                                {basisConversion && (
                                    <p className="mt-1 text-[10px] text-gray-400">
                                        {formatLegacyBasisLabel(basisConversion)}
                                    </p>
                                )}
                                <p className="text-[10px] text-blue-600 mt-1.5 flex items-center gap-1">
                                    <Info size={10} /> Manual input — all downstream figures derive from this
                                </p>
                                {targetError && (
                                    <p role="alert" className="mt-3 max-w-2xl rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                                        {targetError}
                                    </p>
                                )}
                                {targetResult && (
                                    <div role="status" className="mt-3 max-w-3xl space-y-3 rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <p className="font-semibold text-gray-900">
                                                Gross SKU lines: {fmt$(targetResult.gross_lines?.total || 0)} total, {((Number(targetResult.gross_lines?.coverage_pct) || 0) * 100).toFixed(1)}% of target
                                            </p>
                                            {formatTargetDate(targetResult.item?.updatedAt ?? targetResult.item?.updated_at) && (
                                                <span className="text-gray-500">
                                                    Updated {formatTargetDate(targetResult.item?.updatedAt ?? targetResult.item?.updated_at)}
                                                </span>
                                            )}
                                        </div>
                                        {!targetResult.gross_lines?.regenerated && targetResult.gross_lines?.error && (
                                            <p className="text-amber-800">
                                                Gross lines were not regenerated: {targetResult.gross_lines.error}
                                            </p>
                                        )}
                                        {!targetResult.gross_lines?.regenerated && !targetResult.gross_lines?.error && (
                                            <p className="text-gray-600">Gross SKU lines were not rebuilt because this plan is not published.</p>
                                        )}
                                        {targetResult.units_to_target?.status === 'not_reconciled' && (
                                            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                                                Units × planned price cover {((Number(targetResult.gross_lines?.coverage_pct) || 0) * 100).toFixed(1)}% of target. Units-to-target reconciliation is still required.
                                            </p>
                                        )}
                                        <div>
                                            <p className="font-medium text-gray-800">Planned price changes</p>
                                            {targetResult.gross_lines?.price_changes?.length ? (
                                                <ul className="mt-1 list-disc space-y-1 pl-5">
                                                    {targetResult.gross_lines.price_changes.map((change, index) => (
                                                        <li key={`${change.sku}-${change.channel}-${index}`}>
                                                            {change.sku || 'Unknown SKU'} ({change.channel}): {fmt$(change.from)} → {fmt$(change.to)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="mt-1 text-gray-500">No planned SKU prices changed.</p>
                                            )}
                                        </div>
                                        {targetResult.linked_budgets?.length > 0 && (
                                            <div className="space-y-2">
                                                <p className="font-medium text-gray-800">Linked Budget open-month preview</p>
                                                {targetResult.linked_budgets.map((budget) => (
                                                    <div key={budget.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-gray-200 bg-white px-3 py-2">
                                                        <div>
                                                            <p className="font-medium">{budget.name}</p>
                                                            {budget.preview_totals ? (
                                                                <>
                                                                    {budget.open_months?.length > 0 && (
                                                                        <p className="mt-1 text-gray-500">
                                                                            Open months: {budget.open_months.map((month) => `${month.slice(5, 7)}/${month.slice(0, 4)}`).join(', ')}
                                                                        </p>
                                                                    )}
                                                                    <p className="mt-1 text-gray-600">
                                                                        Gross {fmt$(budget.preview_totals.gross.before)} → {fmt$(budget.preview_totals.gross.after)} ({fmt$(budget.preview_totals.gross.delta)}); Discounts {fmt$(budget.preview_totals.discounts.before)} → {fmt$(budget.preview_totals.discounts.after)} ({fmt$(budget.preview_totals.discounts.delta)}); Total income {fmt$(budget.preview_totals.total_income.before)} → {fmt$(budget.preview_totals.total_income.after)} ({fmt$(budget.preview_totals.total_income.delta)})
                                                                    </p>
                                                                </>
                                                            ) : (
                                                                <p className="mt-1 text-amber-800">{budget.error || 'Budget preview unavailable.'}</p>
                                                            )}
                                                        </div>
                                                        <a
                                                            href={`/budget?id=${budget.id}`}
                                                            className="whitespace-nowrap font-medium text-blue-700 underline"
                                                        >
                                                            Review in Budget
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </SectionForecastDemand>
                    {/* ── SECTION 3 — Channel Split ── */}
                    <SectionForecastDemand number="3" title="Channel Split">
                        {catalogTablesBody(
                        <div className="overflow-x-auto" key={`channel-split-${planningId}-${effectiveForecastYearStr || meta?.forecast_year}`}>
                            <table className="w-full border border-gray-200 rounded-lg overflow-hidden [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200 [&_tbody_tr:hover]:bg-[#fafafb]">
                                <thead className="bg-gray-50">
                                    <tr className="text-left text-[11px] text-gray-600 uppercase tracking-wide">
                                        <th className="px-3 py-2" style={{ width: 160 }}>Channel</th>
                                        <th className="px-3 py-2" style={{ width: 130 }}>LTM %</th>
                                        <th className="px-3 py-2" style={{ width: 130 }}>Channel %</th>
                                        <th className="px-3 py-2" style={{ width: 200 }}>Channel Revenue Target</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {channels.map((ch) => {
                                        const othersSum = channels
                                            .filter((c) => c.channel !== ch.channel)
                                            .reduce((s, c) => s + (c.pct ?? 0), 0);
                                        const maxAllowed = Math.max(0, 1 - othersSum);
                                        return (
                                            <tr key={ch.channel} className="border-t border-gray-100">
                                                <td className="px-3 py-2 text-[12px] font-medium">{ch.channel}</td>
                                                <td className="px-3 py-2 bg-gray-50">
                                                    <span className="text-[12px] text-gray-500">{fmtLtmPct(channelLtmFraction(ch.channel))}</span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <CappedPctInputForecastDemand
                                                        value={ch.pct}
                                                        maxAllowed={maxAllowed}
                                                        onSave={(v) => updateChannelPct(ch.channel, v)}
                                                        nullable
                                                        onClear={() => updateChannelPct(ch.channel, null)}
                                                        placeholder="—"
                                                    />
                                                </td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(channelTarget(revenueTarget, ch.pct ?? 0))}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr className="border-t border-gray-200 bg-gray-50/50">
                                        <td className="px-3 py-2 text-[12px] font-semibold">Total</td>
                                        <td className="px-3 py-2 bg-gray-50">
                                            <span className="text-[12px] text-gray-500">{fmtLtmPct(channelSplitTotalLtmFraction)}</span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <ValidationBadgeForecastDemand valid={channelValid} value={fmtPct(channelPctTotal)} />
                                        </td>
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(revenueTarget)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        )}
                    </SectionForecastDemand>
                    {/* ── SECTION 4 — Category Allocation — All Channels ── */}
                    <SectionForecastDemand number="4" title="Category Allocation — All Channels">
                        {catalogTablesBody(
                        <div className="overflow-x-auto">
                            <table className="w-full border border-gray-200 rounded-lg overflow-hidden min-w-[1400px] [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200 [&_tbody_tr:hover]:bg-[#fafafb]">
                                <thead className="bg-gray-50">
                                    <tr className="text-left text-[11px] text-gray-600 uppercase tracking-wide">
                                        <th className="px-3 py-2 min-w-[140px]">
                                            <div className="flex items-center gap-2 justify-between">
                                                <span>Category</span>
                                                {showCategoryAddButton && (
                                                    <button
                                                        ref={categoryAddAnchorRef}
                                                        type="button"
                                                        className="bg-transparent text-primary px-1 py-1 rounded-md"
                                                        onClick={toggleCategoryAdd}
                                                        aria-expanded={categoryAddOpen}
                                                        aria-label="Add category"
                                                    >
                                                        <PlusIcon className="h-4 w-4 text-primary" />
                                                    </button>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-3 py-2 w-[110px]">LTM %</th>
                                        <th className="px-3 py-2 w-[120px]">Category %</th>
                                        <th className="px-3 py-2 w-[200px]">Revenue Target</th>
                                        <th className="px-3 py-2 w-[180px]">{(lastSavedRef?.current?.forecastYear ?? 2025) - 1} Actuals</th>
                                        <th className="px-3 py-2 w-[160px]">Delta vs {(lastSavedRef?.current?.forecastYear ?? 2025) - 1}</th>
                                        <th className="px-3 py-2 w-[200px]">YTD Forecast</th>
                                        <th className="px-3 py-2 w-[180px]">YTD Actuals</th>
                                        <th className="px-3 py-2 w-[160px]">YTD Delta</th>
                                        <th className="px-3 py-2 w-[110px]">Last Closed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categories.map((cat) => {
                                        const pct = cat.pct ?? 0;
                                        const target = categoryTarget(revenueTarget, pct);
                                        const delta2025 = target - cat.actuals_2025;
                                        const ytdFcst = catYtdForecast(pct);
                                        const ytdDelta = cat.ytd_actuals - ytdFcst;
                                        const othersSum = categories
                                            .filter((c) => c.category !== cat.category)
                                            .reduce((s, c) => s + (c.pct ?? 0), 0);
                                        const maxAllowed = Math.max(0, 1 - othersSum);
                                        return (
                                            <tr key={cat.category} className="border-t border-gray-100">
                                                <td className="px-3 py-2 text-[12px] font-medium">
                                                    <div className="flex items-center gap-2 justify-between">
                                                        <span>{cat.category}</span>
                                                        <button
                                                            type="button"
                                                            className="bg-transparent text-primary px-1 py-1 rounded-md"
                                                            onClick={() => {
                                                                setDeleteSubCategory(null);
                                                                setDeleteCategory(cat);
                                                            }}
                                                        >
                                                            <TrashIcon className="h-4 w-4 text-primary" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 bg-gray-50">
                                                    <span className="text-[12px] text-gray-500">{fmtLtmPct(categoryLtmFraction(cat.category))}</span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <CappedPctInputForecastDemand
                                                        value={cat.pct}
                                                        maxAllowed={maxAllowed}
                                                        onSave={(v) => updateCategoryPct(cat.category, v)}
                                                        nullable
                                                        onClear={() => updateCategoryPct(cat.category, null)}
                                                        placeholder="—"
                                                    />
                                                </td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(target)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(cat.actuals_2025)}</td>
                                                <td className={`px-3 py-2 text-[12px] ${deltaClass(delta2025)}`}>{fmtDelta(delta2025)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(ytdFcst)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(cat.ytd_actuals)}</td>
                                                <td className={`px-3 py-2 text-[12px] ${deltaClass(ytdDelta)}`}>{fmtDelta(ytdDelta)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px] text-gray-500">{cat.last_closed_date}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr className="border-t border-gray-200 bg-gray-50/50">
                                        <td className="px-3 py-2 text-[12px] font-semibold">Total</td>
                                        <td className="px-3 py-2 bg-gray-50">
                                            <span className="text-[12px] text-gray-500">{fmtLtmPct(categorySplitTotalLtmFraction)}</span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <ValidationBadgeForecastDemand valid={catValid} value={fmtPct(catPctTotal)} />
                                        </td>
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(revenueTarget)}</td>
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(categories.reduce((s, c) => s + c.actuals_2025, 0))}</td>
                                        <td className={`px-3 py-2 text-[12px] ${deltaClass(revenueTarget - categories.reduce((s, c) => s + c.actuals_2025, 0))}`}>
                                            {fmtDelta(revenueTarget - categories.reduce((s, c) => s + c.actuals_2025, 0))}
                                        </td>
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(ytdForecastTotal)}</td>
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(categories.reduce((s, c) => s + c.ytd_actuals, 0))}</td>
                                        <td className={`px-3 py-2 text-[12px] ${deltaClass(categories.reduce((s, c) => s + c.ytd_actuals, 0) - ytdForecastTotal)}`}>
                                            {fmtDelta(categories.reduce((s, c) => s + c.ytd_actuals, 0) - ytdForecastTotal)}
                                        </td>
                                        <td className="px-3 py-2" />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        )}
                    </SectionForecastDemand>
                    {/* ── SECTION 5 — Sub-Category Allocation — All Channels ── */}
                    <SectionForecastDemand number="5" title="Sub-Category Allocation — All Channels">
                        {catalogTablesBody(
                        <div className="overflow-x-auto">
                            <table className="w-full border border-gray-200 rounded-lg overflow-hidden min-w-[1600px] [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200 [&_tbody_tr:hover]:bg-[#fafafb]">
                                <thead className="bg-gray-50">
                                    <tr className="text-left text-[11px] text-gray-600 uppercase tracking-wide">
                                        <th className="px-3 py-2 min-w-[170px]">
                                            <div className="flex items-center gap-2 justify-between">
                                                <span>Sub-Category</span>
                                                {showSubcategoryAddButton && (
                                                    <button
                                                        ref={subcategoryAddAnchorRef}
                                                        type="button"
                                                        className="bg-transparent text-primary px-1 py-1 rounded-md"
                                                        onClick={toggleSubcategoryAdd}
                                                        aria-expanded={subcategoryAddOpen}
                                                        aria-label="Add sub-category"
                                                    >
                                                        <PlusIcon className="h-4 w-4 text-primary" />
                                                    </button>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-3 py-2 w-[120px]">Category</th>
                                        <th className="px-3 py-2 w-[110px]">LTM %</th>
                                        <th className="px-3 py-2 w-[120px]">% of Channel</th>
                                        <th className="px-3 py-2 w-[120px]">Cat % Check</th>
                                        <th className="px-3 py-2 w-[200px]">Revenue Target</th>
                                        <th className="px-3 py-2 w-[180px]">{(lastSavedRef?.current?.forecastYear ?? 2025) - 1} Actuals</th>
                                        <th className="px-3 py-2 w-[160px]">Delta vs {(lastSavedRef?.current?.forecastYear ?? 2025) - 1}</th>
                                        <th className="px-3 py-2 w-[200px]">YTD Forecast</th>
                                        <th className="px-3 py-2 w-[180px]">YTD Actuals</th>
                                        <th className="px-3 py-2 w-[160px]">YTD Delta</th>
                                        <th className="px-3 py-2 w-[110px]">Last Closed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subCategories.map((sc) => {
                                        const poc = sc.pct_of_channel ?? 0;
                                        const target = subCatTarget(categoryRevTargetFor(sc.category), poc);
                                        const delta2025 = target - sc.actuals_2025;
                                        const ytdFcst = subCatYtdForecast(sc.category, poc);
                                        const ytdDelta = sc.ytd_actuals - ytdFcst;
                                        const catCheckRaw = catPctCheck(sc.category);
                                        const catCheck = Math.min(catCheckRaw, 1);
                                        const catAlloc = categories.find((c) => c.category === sc.category)?.pct ?? 0;
                                        const catCheckValid =
                                            Math.abs(catCheckRaw - catAlloc) < 0.0001 ||
                                            catCheckRaw >= 1 - 0.0001;
                                        const maxAllowed = 2;
                                        return (
                                            <tr key={sc.id} className="border-t border-gray-100">
                                                <td className="px-3 py-2 text-[12px] font-medium">
                                                    <div className="flex items-center gap-2 justify-between">
                                                        <span>{sc.sub_category}</span>
                                                        <button
                                                            type="button"
                                                            className="bg-transparent text-primary px-1 py-1 rounded-md"
                                                            onClick={() => {
                                                                setDeleteCategory(null);
                                                                setDeleteSubCategory(sc);
                                                            }}
                                                        >
                                                            <TrashIcon className="h-4 w-4 text-primary" />
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-[12px] text-gray-500">{sc.category}</td>
                                                <td className="px-3 py-2 bg-gray-50">
                                                    <span className="text-[12px] text-gray-500">{fmtLtmPct(subcategoryLtmFraction(sc.id))}</span>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <CappedPctInputForecastDemand
                                                        value={sc.pct_of_channel}
                                                        maxAllowed={maxAllowed}
                                                        onSave={(v) => updateSubCategoryPct(sc.id, v)}
                                                        nullable
                                                        onClear={() => updateSubCategoryPct(sc.id, null)}
                                                        placeholder="—"
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span className={`text-[12px] ${catCheckValid ? "text-gray-500" : "text-red-600 font-semibold"}`}>
                                                        {fmtPct(catCheck)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(target)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(sc.actuals_2025)}</td>
                                                <td className={`px-3 py-2 text-[12px] ${deltaClass(delta2025)}`}>{fmtDelta(delta2025)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(ytdFcst)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(sc.ytd_actuals)}</td>
                                                <td className={`px-3 py-2 text-[12px] ${deltaClass(ytdDelta)}`}>{fmtDelta(ytdDelta)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px] text-gray-500">{sc.last_closed_date}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr className="border-t border-gray-200 bg-gray-50/50">
                                        <td className="px-3 py-2 text-[12px] font-semibold">Total</td>
                                        <td className="px-3 py-2" />
                                        <td className="px-3 py-2 bg-gray-50">
                                            <span className="text-[12px] text-gray-500">{fmtLtmPct(subcategorySplitTotalLtmFraction)}</span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <ValidationBadgeForecastDemand valid={subCatValid} value={fmtPct(subCatPctTotal)} />
                                        </td>
                                        <td className="px-3 py-2" />
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(revenueTarget)}</td>
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(subCategories.reduce((s, sc) => s + sc.actuals_2025, 0))}</td>
                                        <td className={`px-3 py-2 text-[12px] ${deltaClass(revenueTarget - subCategories.reduce((s, sc) => s + sc.actuals_2025, 0))}`}>
                                            {fmtDelta(revenueTarget - subCategories.reduce((s, sc) => s + sc.actuals_2025, 0))}
                                        </td>
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(ytdForecastTotal)}</td>
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(subCategories.reduce((s, sc) => s + sc.ytd_actuals, 0))}</td>
                                        <td className={`px-3 py-2 text-[12px] ${deltaClass(subCategories.reduce((s, sc) => s + sc.ytd_actuals, 0) - ytdForecastTotal)}`}>
                                            {fmtDelta(subCategories.reduce((s, sc) => s + sc.ytd_actuals, 0) - ytdForecastTotal)}
                                        </td>
                                        <td className="px-3 py-2" />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        )}
                    </SectionForecastDemand>
                    {/* ── SECTION 6 — Monthly Revenue Allocation — CEO Seasonality Curve ── */}
                    <SectionForecastDemand number="6" title="Monthly Revenue Allocation — CEO Seasonality Curve">
                        {tablesLoading ? (
                           <div className="flex min-h-[168px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50/70 px-4 py-14">
                                <LoaderSpinner width="40" height="40" border="3" colorSpinner="gray" />
                            </div>
                        ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full border border-gray-200 rounded-lg overflow-hidden min-w-[1200px] [&_th]:border [&_th]:border-gray-200 [&_td]:border [&_td]:border-gray-200 [&_tbody_tr:hover]:bg-[#fafafb]">
                                <thead className="bg-gray-50">
                                    <tr className="text-left text-[11px] text-gray-600 uppercase tracking-wide">
                                        <th className="px-3 py-2 w-[80px]">Month</th>
                                        <th className="px-3 py-2 w-[140px]">Historical % (Default)</th>
                                        <th className="px-3 py-2 w-[140px]">Override % (Editable)</th>
                                        <th className="px-3 py-2 w-[110px]">Final %</th>
                                        <th className="px-3 py-2 w-[190px]">Monthly Revenue Target</th>
                                        <th className="px-3 py-2 w-[110px]">Actual %</th>
                                        <th className="px-3 py-2 w-[190px]">Actual Revenue</th>
                                        <th className="px-3 py-2 w-[160px]">Delta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {months.map((m, idx) => {
                                        const final = finalMonthPct(m);
                                        const target = monthlyTarget(revenueTarget, m);
                                        const isClosed = isForecastMonthClosed(idx);
                                        const othersSum = months
                                            .filter((mo) => mo.month !== m.month)
                                            .reduce((s, mo) => s + (mo.override_pct ?? 0), 0);
                                        const maxAllowed = Math.max(0, 1 - othersSum);
                                        const delta = isClosed && m.actual_revenue !== null ? m.actual_revenue - target : null;
                                        return (
                                            <tr key={m.month} className={`border-t border-gray-100 ${isClosed ? "bg-gray-50/70" : ""}`}>
                                                <td className="px-3 py-2 text-[12px] font-medium">
                                                    <span className="flex items-center gap-1.5">
                                                        {m.month}
                                                        {isClosed && (
                                                            <span className="text-[9px] text-gray-500 bg-gray-100 px-1 py-0.5 rounded-[3px] uppercase tracking-wide">
                                                                Closed
                                                            </span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">
                                                    {m.historical_pct !== null ? fmtPct(m.historical_pct) : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <CappedPctInputForecastDemand
                                                        value={m.override_pct}
                                                        maxAllowed={maxAllowed}
                                                        onSave={(v) => updateMonthOverride(m.month, v)}
                                                        nullable
                                                        onClear={() => updateMonthOverride(m.month, null)}
                                                        placeholder="—"
                                                    />
                                                </td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmtPct(final)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">{fmt$(target)}</td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">
                                                    {isClosed && m.actual_pct !== null ? fmtPct(m.actual_pct) : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 bg-gray-50 text-[12px]">
                                                    {isClosed && m.actual_revenue !== null ? fmt$(m.actual_revenue) : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-[12px]">
                                                    {delta !== null ? <span className={deltaClass(delta)}>{fmtDelta(delta)}</span> : <span className="text-gray-400">—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr className="border-t border-gray-200 bg-gray-50/50">
                                        <td className="px-3 py-2 text-[12px] font-semibold">Total</td>
                                        <td className="px-3 py-2" />
                                        <td className="px-3 py-2">
                                            <ValidationBadgeForecastDemand
                                                valid={monthValid}
                                                value={fmtPct(monthOverridePctTotalRounded)}
                                            />
                                        </td>
                                        <td className="px-3 py-2" />
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">{fmt$(revenueTarget)}</td>
                                        <td className="px-3 py-2" />
                                        <td className="px-3 py-2 bg-gray-50 text-[12px] font-semibold">
                                            {fmt$(months.reduce((s, m) => s + (m.actual_revenue ?? 0), 0))}
                                        </td>
                                        <td
                                            className={`px-3 py-2 text-[12px] ${deltaClass(
                                                months.reduce((s, m) => s + (m.actual_revenue ?? 0), 0) - ytdForecastTotal
                                            )}`}
                                        >
                                            {fmtDelta(
                                                months.reduce((s, m) => s + (m.actual_revenue ?? 0), 0) - ytdForecastTotal
                                            )}
                                        </td>
                                    </tr>
                                    <tr className="border-t border-gray-100">
                                        <td className="px-3 py-2 text-[11px] text-gray-500">Status</td>
                                        <td colSpan={7} className="px-3 py-2">
                                            {monthValid ? (
                                                <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-emerald-700">
                                                    <Check size={13} /> Valid
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-red-600">
                                                    <AlertTriangle size={13} /> Invalid — Override % must sum to 100%
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        )}
                    </SectionForecastDemand>
                </div>
            </section>
            {categoryAddOpen &&
                showCategoryAddButton &&
                createPortal(
                    <div
                        ref={categoryAddPortalRef}
                        className="fixed z-[100] w-72 rounded-md border border-gray-200 bg-white shadow-lg"
                        style={{
                            top: categoryDropdownPos.top,
                            left: categoryDropdownPos.left,
                            width: FORECAST_ADD_CATEGORY_PANEL_W,
                        }}
                    >
                        <div className="p-2 border-b border-gray-100">
                            <input
                                type="text"
                                className="w-full rounded border border-gray-200 px-2 py-1.5 text-[12px]"
                                placeholder="Search categories…"
                                value={categoryAddQuery}
                                onChange={(e) => setCategoryAddQuery(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <div className="max-h-56 overflow-y-auto py-1">
                            {catalogLoading ? (
                                <div className="flex justify-center py-6">
                                    <LoaderSpinner width="28" height="28" border="3" colorSpinner="gray" />
                                </div>
                            ) : availableCategoriesToAdd.length === 0 ? (
                                <p className="px-3 py-2 text-[11px] text-gray-500">
                                    No categories left to add for this year.
                                </p>
                            ) : (
                                availableCategoriesToAdd.map((c) => (
                                    <button
                                        key={String(c.categoryId ?? c.categoryName)}
                                        type="button"
                                        className="w-full px-3 py-2 text-left text-[12px] hover:bg-gray-50"
                                        onClick={() => addPickedCategory(c)}
                                    >
                                        {c.categoryName}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>,
                    document.body
                )}
            {subcategoryAddOpen &&
                showSubcategoryAddButton &&
                createPortal(
                    <div
                        ref={subcategoryAddPortalRef}
                        className="fixed z-[100] w-80 rounded-md border border-gray-200 bg-white shadow-lg"
                        style={{
                            top: subcategoryDropdownPos.top,
                            left: subcategoryDropdownPos.left,
                            width: FORECAST_ADD_SUBCATEGORY_PANEL_W,
                        }}
                    >
                        <div className="p-2 border-b border-gray-100">
                            <input
                                type="text"
                                className="w-full rounded border border-gray-200 px-2 py-1.5 text-[12px]"
                                placeholder="Search sub-categories…"
                                value={subcategoryAddQuery}
                                onChange={(e) => setSubcategoryAddQuery(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <div className="max-h-56 overflow-y-auto py-1">
                            {catalogLoading ? (
                                <div className="flex justify-center py-6">
                                    <LoaderSpinner width="28" height="28" border="3" colorSpinner="gray" />
                                </div>
                            ) : availableSubcategoriesToAdd.length === 0 ? (
                                <p className="px-3 py-2 text-[11px] text-gray-500">
                                    No sub-categories left to add for this year.
                                </p>
                            ) : (
                                availableSubcategoriesToAdd.map((s) => (
                                    <button
                                        key={String(s.subcategoryId)}
                                        type="button"
                                        className="w-full px-3 py-2 text-left text-[12px] hover:bg-gray-50"
                                        onClick={() => addPickedSubcategory(s)}
                                    >
                                        <span className="font-medium">{s.subcategoryName}</span>
                                        <span className="block text-[10px] text-gray-500">{s.categoryName}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>,
                    document.body
                )}
            <ConfrimModal
                isOpen={deleteCategory != null || deleteSubCategory != null}
                isLoading={deleteSaving}
                title={
                    deleteCategory
                        ? `Delete category "${deleteCategory.category}" for forecast year ${meta?.forecast_year}? All sub-categories under this category will also be removed from saved data for this year.`
                        : deleteSubCategory
                          ? `Delete sub-category "${deleteSubCategory.sub_category}" for forecast year ${meta?.forecast_year}?`
                          : ""
                }
                confirmButtonTitle="Yes, delete"
                submitClick={submitDeleteModal}
                cancelClick={closeDeleteModal}
                size="max-w-lg"
                testId="forecast-demand-delete-category-subcategory"
            />
            {pendingRevenueTarget && createPortal(
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/30 p-4">
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="revenue-target-confirm-title"
                        data-testid="forecast-demand-revenue-target-confirm"
                        className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg bg-white p-6 shadow-xl"
                    >
                        <h2 id="revenue-target-confirm-title" className="text-xl font-semibold text-gray-900">
                            Confirm Gross Sales Target change
                        </h2>
                        <p className="mt-2 text-sm text-gray-700">
                            {fmt$(pendingRevenueTarget.from)} → {fmt$(pendingRevenueTarget.to)}
                        </p>
                        <p className="mt-1 text-xs font-medium text-gray-600">
                            Basis: {(forecastDemandPlanningData?.gross_basis || selectedForecastDemandPlanning?.gross_basis) === 'true_gross'
                                ? 'True Gross Sales'
                                : 'Workbook'}
                        </p>
                        <div className="mt-5 space-y-3 text-sm">
                            <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
                                <p className="font-semibold text-emerald-900">Will update</p>
                                <p className="mt-1 text-emerald-800">
                                    Plan target; monthly and channel dollars (current percentages kept); published gross SKU lines (rebuilt).
                                </p>
                            </div>
                            <div className="rounded border border-amber-200 bg-amber-50 p-3">
                                <p className="font-semibold text-amber-900">Will not update</p>
                                <p className="mt-1 text-amber-800">
                                    Linked Budget(s): {targetBudgetLookupLoading
                                        ? 'loading names'
                                        : targetBudgetNames.length
                                            ? targetBudgetNames.join(', ')
                                            : targetBudgetLookupError
                                                ? 'names unavailable'
                                                : 'none'}
                                    . They will show as out of date; review and apply the refresh in Budget.
                                </p>
                                {targetBudgetLookupError && (
                                    <p className="mt-1 text-xs text-amber-800">{targetBudgetLookupError}</p>
                                )}
                                <p className="mt-1 font-medium text-amber-900">Closed months never change.</p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2 border-t border-gray-100 pt-4">
                            <button
                                type="button"
                                onClick={cancelRevenueTargetChange}
                                disabled={targetSaving}
                                className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmRevenueTargetChange}
                                disabled={targetSaving || targetBudgetLookupLoading}
                                className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                            >
                                {targetSaving ? 'Updating…' : 'Yes, update target'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    )
}